import {
  differenceInMinutes,
  addMinutes,
  differenceInSeconds,
  addSeconds,
  isBefore,
  addDays,
  startOfDay,
} from "date-fns";
import ringSound from "../assets/ring.wav";
import { v1 as uuid } from "uuid";
import StatefulSettings, {
  STATE_KEY_REPORTCOMPLETE,
  STATE_KEY_CURRENT_DAY,
  STATE_KEY_HISTORY_POSITIONS,
  STATE_KEY_HISTORY_PENALTIES,
  STATE_KEY_PROOF_REQUESTS,
  STATE_KEY_CUSTOM_DAY,
} from "./StatefulSettings";
import {
  IEventItem,
  BaseItem,
  EventItem,
  EventBasketItem,
  TaskItem,
  ITaskTimerInfo,
  TaskBasketItem,
  IEventTimerInfo,
  IEventCustomDecisionResponse,
  PenaltyItem,
  ISchedulerTimerInfo,
  IPenaltyItem,
  IPendingItem,
  ISettingsItem,
  IPositionBasketInfo,
  PenaltyBasketItem,
  ITaskItem,
  ITaskBasketInfo,
  IScheduleOptions,
  ItemAdditionOptions,
  IRollResults,
  IRollPenalty,
  RollState,
  PermissionItem,
  IPermissionItem,
  IPermissionTimerInfo,
  PermissionBasketItem,
  IEventDecisionResponse,
  BaseBasketItem,
  ITimedBasketInfo,
  IShowNotificationState,
  IRouletteRequest,
  PilloryTopic,
  VoteTask,
  IPenaltyTimerInfo,
  ITaskProofRequestItem,
  IPenaltyProofRequestItem,
  AuditMessage,
  LovenseCommand,
  IRequestLockState,
  IOptionSupport,
  ITaskVoteConfig,
  ICustomSchedule,
  ITypingStats,
  PositionBasketItem,
  PositionItem,
  ICustomScheduleEvent,
  ISiteEffectsSettings,
} from "../ts/Definitions";
import {
  WeekendHandling,
  CountType,
  PreselectType,
  AvailabilityType,
  ControlType,
  PendingUpdateType,
  AuditActionTypes,
  ItemTypes,
  PenaltyType,
  EventType,
  TaskType,
  PenaltyPointConversionType,
  ReportStyle,
  AvailabilityDayType,
  ProofStatus,
  LovensePresetName,
  TraceLevel,
} from "../ts/enums";
import Cookies, { COOKIE_KEY_POSITION_TICKS } from "../utilities/cookies";
import {
  MessageType,
  NotificationType,
  sendDailyReport,
  sendExceptionMessage,
  sendLinesToOwner,
  sendMessageToOwner,
  sendPilloryCompleteMessageToOwner,
  sendRequestProof,
  sendRouletteAddedMessageToOwner,
  sendRoulettePilloryAddedMessageToOwner,
  sendRouletteVoteAddedMessageToOwner,
} from "./messaging";
import uniqueId from 'lodash/uniqueId';
import concat from 'lodash/concat';
import Emitter, {
  POSITION_TIMER_COMPLETE,
  METRONOME_TIMER_COMPLETE,
  SITE_EFFECTS_TIMER_COMPLETE,
  ShowLovenseHelpDialog,
  ShowMetronomeDialog,
  ShowSiteEffectsOverlay,
} from "../utilities/Emitter";
import { FEATURE_FLAG_TRACE_LOGS } from "../utilities/ConfigCache";
import {
  canHandleDiscordCalls,
  handleAttendanceRequest,
  handleDiscordLovenseTicker,
  handleDiscordRequestProofTicker,
  handleDiscordVoteTicker,
  handleStartingTask,
  sendFailedMessage,
} from "../utilities/discord";
import UIFx from "uifx";
import { pushLovense } from "../utilities/externalApiUtils";
import {
  isAvailableItem,
  getRandom,
  getRandomItemWithCache,
  getScheduleReason,
  isTimedItem,
  getDefaultVoteCount,
  getVoteCountType,
  getCountMinutes,
  convertToCountTypeString,
  flipCoin,
  getRandomItem,
  getDecimalRandom,
  getPilloryTopicDefaults,
  getRandomCountForItem,
  getItemTypeDisplayName,
  cloneObject,
} from "../utilities/utils";
import Timer from "../utilities/Timer";
import {
  convertToEventDayFromDate,
  convertToMinutes,
  formatValidDate,
  scheduleEvent,
  schedulePermission,
  scheduleTask,
} from "../utilities/dateUtils";
import { TaskManager } from "../services/TaskManager";
import { EventProcessor } from "../services/EventProcessor";
import { PenaltyManager } from "../services/PenaltyManager";
import { VotingManager } from "../services/VotingManager";
import { PositionTracker } from "../services/PositionTracker";
import { DayRollManager } from "../services/DayRollManager";
import { EventScheduleManager } from "../services/EventScheduleManager";
import { RouletteManager } from "../services/RouletteManager";
import { TaskSelectionManager } from "../services/TaskSelectionManager";
import { resolveSiteFxMedia } from "../utilities/siteFx";

const ring = new UIFx(ringSound);

// TEMPORARY: cold-start load performance logging — remove before next deploy
const loadPerf = (label: string, ...extra: unknown[]) => {
  if (typeof performance === 'undefined') return;
  if (import.meta.env.VITE_ENABLE_LOAD_PERF !== 'true') return;
  const g = globalThis as any;
  if (!g.__loadPerfT0) g.__loadPerfT0 = performance.now();
  console.log(`[LOAD_PERF] +${(performance.now() - g.__loadPerfT0).toFixed(0)}ms | ${label}`, ...extra);
};

const MORNING_REPORT_ID = "MORNING_REPORT_ID";
const DAILY_REPORT_ID = "DAILY_REPORT_ID";

enum SchedulerState {
  Uninitialised = "uninitialised",
  Initialising = "initialising",
  Ready = "ready",
  RollingDay = "rollingDay",
  RefreshingTasks = "refreshingTasks",
  InitialisingDay = "initialisingDay",
  AddingEnforcedTasks = "addingEnforcedTasks"
}

export default class Scheduler {
  private settings: StatefulSettings;
  private showMessage: (args: IShowNotificationState) => void;
  private cookies: Cookies;
  private minuteTimer: Timer;
  private state = SchedulerState.Uninitialised;
  private initialOwnerLoaded: string | undefined = undefined;
  private justAddedTask = false;
  private handledFirstPendingItemParse = false;
  private pendingItemsTransaction?: string;
  private isDisposed = false;
  private initializationPromise?: Promise<void>;
  private taskManager: TaskManager;
  private eventProcessor: EventProcessor;
  private penaltyManager: PenaltyManager;
  private votingManager: VotingManager;
  private positionTracker: PositionTracker;
  private dayRollManager: DayRollManager;
  private eventScheduleManager: EventScheduleManager;
  private rouletteManager: RouletteManager;
  private taskSelectionManager: TaskSelectionManager;

  constructor(
    showMessage: (args: IShowNotificationState) => void,
    userSettings: StatefulSettings,
    cookiesService: Cookies,
    minuteTimer: Timer
  ) {
    this.showMessage = showMessage;
    this.settings = userSettings;
    this.settings.addDisposedListener(this.settingsDisposed);
    this.cookies = cookiesService;
    this.minuteTimer = minuteTimer;
    this.taskManager = new TaskManager();
    this.taskManager.initialize(this.settings, this.showMessage);
    this.eventProcessor = new EventProcessor();
    this.eventProcessor.initialize(this.settings, this.showMessage, this.triggerItemsAwaitingEvent, this.handleAutoProofRequest);
    this.penaltyManager = new PenaltyManager();
    this.penaltyManager.initialize(this.settings);
    this.votingManager = new VotingManager();
    this.votingManager.initialize(this.settings, this.showMessage);
    this.positionTracker = new PositionTracker();
    this.positionTracker.initialize(
      this.settings,
      this.showMessage,
      this.cookies,
      this.auditIncrement,
      this.auditDecrement,
      this.addDailyRollPenalty
    );
    this.dayRollManager = new DayRollManager(
      this.settings,
      this.showMessage,
      this.auditDecrement,
      this.onTaskCompleted,
      this.triggerItemsAwaitingEvent,
      this.removeItemFromSchedule,
      this.checkForCompleteTaskVotes,
      this.handlePendingItems,
      this.handlePositionTick,
      this.tidyTaskListOnRoll,
      this.initialiseNewDay,
      this.eventProcessor.onEventComplete
    );
    this.eventScheduleManager = new EventScheduleManager(
      this.settings,
      this.eventProcessor.onEventComplete,
      this.addPermissionToBasket,
      this.handleMissedScheduleItem,
      this.handleDailyReportRequest,
      this.handleMorningReportRequest,
      this.onPermissionRequested,
      this.onStartOfDay,
      this.handleAutoProofRequest,
      () => this.state,
      this.handleScheduledAttendance
    );
    this.rouletteManager = new RouletteManager(
      this.settings,
      this.showMessage,
      this.onTaskCompleted,
      this.onAddToSchedule
    );
    this.taskSelectionManager = new TaskSelectionManager(
      this.settings,
      this.scheduleAddTaskToBasket,
      this.addPermanentTask,
      this.handlePermanentTaskRemoval,
      () => this.state,
      (state: string) => { this.state = state as SchedulerState; }
    );
  }

  /**
   * Initializes the scheduler and subscribes to necessary events.
   * Must be called after construction to start the scheduler.
   */
  public init(): void {
    loadPerf(`Scheduler.init called`, { settingsLoaded: this.settings.isLoaded() });
    if (this.isDisposed) {
      console.error("Cannot initialize disposed scheduler");
      return;
    }
    if (this.state !== SchedulerState.Uninitialised) return;
    this.settings.subscribeToTaskLocks(this.settings.handleCompletedLockTasks);
    if (this.settings.isLoaded()) {
      this.initialiseSession().catch((error) => {
        console.error("Failed to initialize session:", error);
        this.settings.auditUser(
          new AuditMessage(
            AuditActionTypes.Session,
            "Initialization Failed",
            `Session initialization error: ${error instanceof Error ? error.message : String(error)}`,
            TraceLevel.error
          )
        );
      });
    } else {
      this.settings.subscribeToLoaded(this.initialiseSession);
    }
  }

  public getSettings = () => {
    return this.settings;
  };

  private settingsDisposed = () => {
    this.closeDown();
  };

  public closeDown = (): void => {
    if (this.isDisposed) return;
    this.isDisposed = true;

    try {
      this.positionTracker?.dispose();
      this.minuteTimer?.unsubscribe(this.handleMinuteTick);
      this.settings?.unsubscribeToTaskLocks(
        this.settings.handleCompletedLockTasks
      );
      this.settings?.unsubscribeToLoaded(this.initialiseSession);
      this.settings?.unsubscribeToControlRecord(this.checkOwnerChanged);
      this.settings?.setPendingStateReadyCallback(undefined);
    } catch (error) {
      console.error("Error during scheduler cleanup:", error);
      this.settings?.auditUser(
        new AuditMessage(
          AuditActionTypes.Session,
          "Scheduler Cleanup",
          `Error during cleanup: ${error instanceof Error ? error.message : String(error)}`,
          TraceLevel.error
        )
      );
    }
  };

  private checkOwnerChanged = (): void => {
    const { username } = this.settings.getControl().getOwner();
    if (this.initialOwnerLoaded && this.initialOwnerLoaded !== username) {
      this.initialOwnerLoaded = username;
      this.showMessage({
        message: "Your session has been taken over by " + username,
        persist: true,
        notificationType: NotificationType.General,
      });
    } else if (!this.initialOwnerLoaded && username) {
      this.initialOwnerLoaded = username;
    }
  };

  /*  getters */

  /* initialisation methods of state */
  // sets up the session for the user when they have never used the app before or if they are reopening a closed session
  private initialiseSession = async (): Promise<void> => {
    loadPerf(`Scheduler.initialiseSession called`);
    if (this.isDisposed) return;
    if (this.initializationPromise) return this.initializationPromise;
    if (this.state !== SchedulerState.Uninitialised) return;

    this.initializationPromise = (async () => {
      this.state = SchedulerState.Initialising;
      try {
        this.settings.unsubscribeToLoaded(this.initialiseSession);
        this.settings.subscribeToControlRecord(this.checkOwnerChanged);

        // Initialize all components
        this.initialiseDaily();
        this.initialiseCredits();
        this.initialisePosition();
        this.initialiseSchedule();

        await this.handleMinuteTick();

        if (!this.isDisposed) {
          this.minuteTimer.subscribe(this.handleMinuteTick);
          this.state = SchedulerState.Ready;
          loadPerf(`Scheduler.initialiseSession complete → state=Ready`);
          // Process any pending items immediately when Firebase notifies us of changes,
          // rather than waiting up to a minute for the next tick.
          this.settings.setPendingStateReadyCallback(() => {
            if (this.state === SchedulerState.Ready) {
              this.handlePendingItems();
            }
          });
        }
      } catch (error) {
        this.state = SchedulerState.Uninitialised;
        console.error("Session initialization failed:", error);
        this.settings.auditUser(
          new AuditMessage(
            AuditActionTypes.Session,
            "Session Initialization",
            `Failed to initialize: ${error instanceof Error ? error.message : String(error)}`,
            TraceLevel.error
          )
        );
        throw error;
      } finally {
        this.initializationPromise = undefined;
      }
    })();

    return this.initializationPromise;
  };

  // re-parses the saved state from json into typed data we can then use
  private initialiseSchedule = (): void => {
    let schedulerInfo = this.settings.getScheduleInfo();
    if (schedulerInfo) {
      schedulerInfo = schedulerInfo.filter(
        (i) => i !== undefined && i !== null
      );
      this.settings.replaceSchedulerState(schedulerInfo);
    }
  };

  // parses the json state into typed state and starts the position timer if there is a position in progress
  private initialisePosition = (): void => {
    this.positionTracker.initialisePosition();
  };

  // ensures the credit data is parsed and sets the timer ticks based on now
  private initialiseCredits = (): void => {
    const now = new Date();
    const creditInfo = this.settings.getTaskSchedule();
    if (creditInfo) {
      for (let i = 0; i < Object.keys(creditInfo).length; i += 1) {
        const key = Object.keys(creditInfo)[i];
        const timer = creditInfo[key];
        if (!timer || !timer.endTime || timer.isComplete) continue;
        const newTicks = differenceInMinutes(timer.endTime, now);
        if (timer.isPaused && timer.timerTicks) {
          const newEndTime = addMinutes(now, timer.timerTicks);
          timer.endTime = newEndTime;
        } else {
          timer.timerTicks = newTicks;
        }
        this.settings.updateTaskState(timer);
      }
    }
  };

  // parses general state from json into typed and catches the change in day
  private initialiseDaily = (): void => {
    const currentDay = this.settings.getState(STATE_KEY_CURRENT_DAY, 0);
    if (!currentDay) {
      this.setStateToCurrentDay();
    }
  };

  public setStateToCurrentDay = (): void => {
    const now = new Date();
    this.settings.updateCurrentDay(startOfDay(now).toISOString());
  };

  /* audit events */

  // audits an event request
  private auditEvent(item: EventItem, available: boolean): void {
    this.settings.auditUser(
      new AuditMessage(
        AuditActionTypes.Event,
        item.name,
        "Requested",
        TraceLevel.info,
        available
      )
    );
  }
  private auditPermission(item: PermissionItem, available: boolean): void {
    this.settings.auditUser(
      new AuditMessage(
        AuditActionTypes.Permission,
        item.name,
        "Requested",
        TraceLevel.info,
        available
      )
    );
  }
  // an event has completed
  private auditEventComplete(item: EventBasketItem): void {
    this.settings.auditUser(
      new AuditMessage(
        AuditActionTypes.Event,
        item.name,
        `Decision: ${item.getBasketText()}`,
        TraceLevel.info,
        item.decision
      )
    );
  }
  // an event has completed
  private auditPermissionComplete(item: PermissionBasketItem): void {
    this.settings.auditUser(
      new AuditMessage(
        AuditActionTypes.Permission,
        item.name,
        `Decision: ${item.getBasketText()}`,
        TraceLevel.info,
        item.decision
      )
    );
  }
  // an amount increment
  private auditIncrement(name: string, action: string, credit: number): void {
    this.settings.auditUser(
      new AuditMessage(
        AuditActionTypes.Task,
        name,
        action,
        TraceLevel.info,
        undefined,
        Math.abs(credit)
      )
    );
  }
  // an amount decrement
  private auditDecrement(name: string, action: string, cost: number): void {
    this.settings.auditUser(
      new AuditMessage(
        AuditActionTypes.Task,
        name,
        action,
        TraceLevel.info,
        undefined,
        Math.abs(cost) * -1
      )
    );
  }

  /* REFRESH METHODS */
  // runs through the credits and applies them if the timer ran out if they are timed
  // this maybe fires many times if the thread has been suspended on a background tab and then switched back to
  private refreshTasks = (): void => {
    const now = new Date();
    const taskSchedule = this.settings.getTaskSchedule();
    const keys = Object.keys(taskSchedule);
    for (let i = 0; i < keys.length; i += 1) {
      const key = keys[i];
      const taskInfo = taskSchedule[key];
      if (!taskInfo) continue;
      if (
        taskInfo.isPaused ||
        taskInfo.isComplete ||
        (taskInfo as ITaskTimerInfo).permanent
      )
        continue;
      const endTime = taskInfo.endTime || now;
      const newTicks = differenceInMinutes(endTime, now);
      const eventScheduled = this.settings.checkSettingItemEventTriggerValid(
        taskInfo
      );
      if (!eventScheduled && (isNaN(newTicks) || newTicks <= 0)) {
        const taskName = taskInfo.name;
        this.onTaskCompleted(key);
        // special handling to play sound when countdown task has completed.
        if (this.settings.getIsSoundEnabled()) ring.play();
        Emitter.emitNotification({
          message: `Timed Task ${taskName} Complete`,
          success: true,
          notificationType: NotificationType.ScheduleInfo,
        });
      } else {
        taskInfo.timerTicks = newTicks < 0 ? 0 : newTicks;
        this.settings.updateTaskState(taskInfo);
      }
    }
  };

  private onTaskCompleted = (key: string, notifyUI = true): number => {
    const reward = this.taskManager.onTaskCompleted(key, notifyUI);
    // on permission complete, handle if it should re enable again
    const taskInfo = this.settings.getTaskSchedule();
    if (taskInfo[key]) {
      this.onPermissionExpiry(taskInfo[key]);
      this.checkForTaskReceiptUnlocks(false, key);
    }
    return reward;
  };

  private onPermissionExpiry = (
    task: ITaskTimerInfo | IPenaltyTimerInfo
  ): void => {
    if (task.itemType !== ItemTypes.permission) return;
    const permissionKey = task.key.split(",")[0];
    const permissionsState = this.settings.getPermissionInfo();
    const permissionItem = permissionsState[permissionKey];
    if (!permissionItem || !permissionItem.isTimed) {
      if (!permissionItem) {
        this.settings.auditUser(
          new AuditMessage(
            AuditActionTypes.Permission,
            task.name || "Unknown",
            `Permission item not found: ${permissionKey}`,
            TraceLevel.warning
          )
        );
      }
      return;
    }
    permissionItem.expired = true;
    permissionItem.isComplete = true;
    permissionItem.completedTime = new Date();
    this.settings.auditUser(
      new AuditMessage(
        AuditActionTypes.Permission,
        permissionItem.name,
        "Approval Expired",
        TraceLevel.info
      )
    );
    this.showMessage({
      message: "Permissions Expired",
      notificationType: NotificationType.ScheduleInfo,
    });
    this.settings.updatePermissionState(permissionItem);
  };

  // checks if position is complete and re-iniitlises if more than one position required
  public refreshPosition = (
    currentPositionCount?: number,
    currentPosition?: string
  ): void => {
    this.positionTracker.refreshPosition(currentPositionCount, currentPosition);
  };

  public refreshMetronome = (currentMetronomeCount?: number): void => {
    const noMetronome = {
      name: "",
      isComplete: true,
      completedTime: new Date(),
      isPaused: false,
      description: "",
      hasPosition: false,
      hasMetronome: false,
      img: "",
      itemType: ItemTypes.position,
      key: "",
      expectedCount: 0,
      initialCount: 0,
      countType: CountType.minutes,
      availabilityDayType: AvailabilityDayType.AllDays,
      scheduleReason: "",
    } as IPositionBasketInfo;

    const handle = this.settings.canPerformSomeTasksForToday();
    const todayDate = new Date();
    const dateDay = convertToEventDayFromDate(todayDate.getDay());
    const customSchedule = this.settings.getScheduledCustomSchedule(dateDay, todayDate);

    if (!handle || !customSchedule?.metronomeEnabled) {
      this.settings.updateMetronomeState(noMetronome);
      return;
    }

    const configuredCount = currentMetronomeCount ?? customSchedule.metronomeCount ?? 0;
    if (configuredCount <= 0) {
      this.settings.updateMetronomeState(noMetronome);
      return;
    }

    const existingInfo = this.settings.getMetronomeInfo();
    const metronomeTask = customSchedule.metronomeTaskKey
      ? this.settings.getTask(customSchedule.metronomeTaskKey)
      : undefined;

    const metronomeInfo = {
      ...existingInfo,
      name: metronomeTask?.name
        ? `Metronome – ${metronomeTask.name}`
        : "Metronome Practice",
      description: metronomeTask?.description?.trim()?.length
        ? metronomeTask.description
        : "Complete the metronome timer for this custom schedule day.",
      hasPosition: false,
      hasMetronome: true,
      isComplete: false,
      isPaused: true,
      countType: CountType.minutes,
      itemType: ItemTypes.position,
      key: existingInfo?.key || "custom-metronome",
      img: customSchedule.metronomeImg ?? metronomeTask?.img ?? "",
      imgId: customSchedule.metronomeImgId ?? metronomeTask?.imgId,
      availabilityDayType: AvailabilityDayType.AllDays,
      expectedCount: configuredCount,
      initialCount: existingInfo?.initialCount || configuredCount,
      taskKey: customSchedule.metronomeTaskKey,
      audioUrl: customSchedule.metronomeAudioUrl,
      scheduleReason: `Custom schedule metronome for ${customSchedule.name}`,
    } as IPositionBasketInfo;
    delete metronomeInfo.endTime;
    delete metronomeInfo.duration;
    delete metronomeInfo.intervalMs;
    this.settings.updateMetronomeState(metronomeInfo);
  };

  public refreshSiteEffects = (currentSiteEffectsCount?: number): void => {
    const noSiteEffects = {
      name: "",
      isComplete: true,
      completedTime: new Date(),
      isPaused: false,
      description: "",
      hasPosition: false,
      hasSiteEffects: false,
      img: "",
      itemType: ItemTypes.position,
      key: "",
      expectedCount: 0,
      initialCount: 0,
      countType: CountType.minutes,
      availabilityDayType: AvailabilityDayType.AllDays,
      scheduleReason: "",
    } as IPositionBasketInfo;

    const handle = this.settings.canPerformSomeTasksForToday();
    const todayDate = new Date();
    const dateDay = convertToEventDayFromDate(todayDate.getDay());
    const customSchedule = this.settings.getScheduledCustomSchedule(dateDay, todayDate);

    if (!handle || !customSchedule?.siteEffectsEnabled) {
      this.settings.updateSiteEffectsState(noSiteEffects);
      return;
    }

    const configuredCount = currentSiteEffectsCount ?? customSchedule.siteEffectsCount ?? 0;
    if (configuredCount <= 0) {
      this.settings.updateSiteEffectsState(noSiteEffects);
      return;
    }

    const existingInfo = this.settings.getSiteEffectsInfo();
    const siteEffectsInfo = {
      ...existingInfo,
      name: "Site FX",
      description: "Complete the Site FX timer for this custom schedule day.",
      hasPosition: false,
      hasMetronome: false,
      hasSiteEffects: true,
      isComplete: false,
      isPaused: true,
      countType: CountType.minutes,
      itemType: ItemTypes.position,
      key: existingInfo?.key || "custom-site-effects",
      img: customSchedule.siteEffectsImg || "",
      imgId: customSchedule.siteEffectsImgId,
      availabilityDayType: AvailabilityDayType.AllDays,
      expectedCount: configuredCount,
      initialCount: existingInfo?.initialCount || configuredCount,
      duration: customSchedule.siteEffectsDurationSec,
      siteEffectsSettings: customSchedule.siteEffectsSettings,
      scheduleReason: `Custom schedule Site FX for ${customSchedule.name}`,
    } as IPositionBasketInfo;
    delete siteEffectsInfo.endTime;
    this.settings.updateSiteEffectsState(siteEffectsInfo);
  };

  private sendTaskToVoting = (
    item: ITaskTimerInfo | IPenaltyTimerInfo,
    requiredMinutes: number
  ) => {
    this.votingManager.sendTaskToVoting(item, requiredMinutes);
  };

  // starts the count down on a penalty item and audits the action
  private startTaskTimer = (
    item: IPenaltyTimerInfo | ITaskTimerInfo,
    capturedImage?: boolean,
    isResumedTask?: boolean
  ): void => {
    const { key } = item;
    const creditInfo = this.settings.getTaskSchedule();
    if (!creditInfo[key]) return;
    creditInfo[key].startedTime = new Date();
    const startToken = creditInfo[key].startedTime.getTime();
    const now = new Date();
    let expectedCount = creditInfo[key].expectedCount;
    let countType = creditInfo[key].countType;
    if (isResumedTask) {
      expectedCount = item.expectedCount ?? expectedCount;
      countType = item.countType ?? countType;
    }
    void this.getSettings()
      .getConfig()
      .fetchConfig()
      .then((config) => {
        const currentTask = this.settings.getTaskSchedule()[key];
        if (!currentTask) return;
        const currentStartToken = currentTask.startedTime
          ? new Date(currentTask.startedTime).getTime()
          : undefined;
        if (currentStartToken !== startToken) return;

        if (config.allowLocks && !capturedImage) {
          const itemDef =
            (this.settings.getCurrentItem(item) as ITaskItem | IPenaltyItem) ||
            item;
          if (
            !isResumedTask &&
            itemDef &&
            itemDef.captureLockImage &&
            canHandleDiscordCalls(
              this.settings.getConfig(),
              this.settings.getControl().username
            )
          ) {
            this.settings.updateRequestLockDialog({
              item: currentTask,
              isPaused: false,
              captureUpload: true,
              result: undefined,
            });
            return;
          }
        }
        if (this.settings.checkSettingItemEventTriggerValid(currentTask)) {
          const schedulerInfo = this.settings.getScheduleInfo();
          const event = this.settings.getEvent(
            currentTask.endTriggerEvent as string
          ) as IEventItem;
          const eventTask = schedulerInfo.find(
            (i) => i.key === currentTask.endTriggerEvent
          );
          if (!eventTask || !eventTask.startTime) {
            // we are a event trigger so remove any counter we might have
            delete currentTask.expectedCount;
            this.settings.auditUser(
              new AuditMessage(
                AuditActionTypes.Alert,
                item.name,
                `Task started until ${event.name} -The item ${item.name} is set to expire on an event which is not scheduled for today. However the event is enabled in the system. This task will roll over until triggered`,
                TraceLevel.warning
              )
            );
          } else {
            this.settings.auditUser(
              new AuditMessage(
                this.getAuditActionType(item.itemType),
                item.name,
                `Task started until ${event.name} - ${currentTask.selectedCondition || ""
                }`,
                TraceLevel.info
              )
            );
          }
        } else if (
          currentTask.endTriggerEvent &&
          (!currentTask.expectedCount ||
            currentTask.countType === CountType.none)
        ) {
          // no count set as backup so fallback to random hours
          const randomHours = getRandom(6, 8);
          const requiredMinutes = getCountMinutes(randomHours, CountType.hours);
          delete currentTask.endTriggerEvent;
          delete currentTask.endTriggerEventName;
          currentTask.expectedCount = requiredMinutes;
          currentTask.countType = CountType.hours;
          currentTask.timerTicks = requiredMinutes;
          currentTask.endTime = addMinutes(now, requiredMinutes);
          this.settings.auditUser(
            new AuditMessage(
              AuditActionTypes.Alert,
              item.name,
              `The item ${item.name} is set to expire on an event which is not enabled and no count or count type set to fallback. Defaulting to between 6 - 8 hours`,
              TraceLevel.warning
            )
          );
          this.settings.auditUser(
            new AuditMessage(
              this.getAuditActionType(item.itemType),
              item.name,
              `Time Started - ${convertToCountTypeString(
                requiredMinutes,
                CountType.minutes
              )}.${" " + (currentTask.selectedCondition || "")}`,
              TraceLevel.info
            )
          );
          this.sendTaskToVoting(currentTask, requiredMinutes);
        } else if (expectedCount && countType) {
          // no event trigger so use count as entered
          currentTask.timerTicks = expectedCount;
          currentTask.endTime = addMinutes(now, expectedCount);
          this.settings.auditUser(
            new AuditMessage(
              this.getAuditActionType(item.itemType),
              item.name,
              `Time Started - ${convertToCountTypeString(
                expectedCount,
                CountType.minutes
              )}.${" " + (currentTask.selectedCondition || "")}`,
              TraceLevel.info
            )
          );
          this.sendTaskToVoting(currentTask, expectedCount);
        } else {
          this.settings.auditUser(
            new AuditMessage(
              this.getAuditActionType(item.itemType),
              item.name,
              `Treating task as simple do and done.${" " + (currentTask.selectedCondition || "")
              }`,
              TraceLevel.info
            )
          );
        }
        currentTask.isPaused = false;
        this.settings.updateTaskState(currentTask);
        this.settings.fireListeners();
      });
  };

  private getAuditActionType = (type: ItemTypes): AuditActionTypes => {
    switch (type) {
      case ItemTypes.task:
        return AuditActionTypes.Task;
      case ItemTypes.event:
        return AuditActionTypes.Event;
      case ItemTypes.penalty:
        return AuditActionTypes.Penalty;
      case ItemTypes.permission:
        return AuditActionTypes.Permission;
      case ItemTypes.position:
        return AuditActionTypes.Position;
    }
    return AuditActionTypes.Session;
  };

  private startLovenseTask = (
    item: TaskBasketItem | PenaltyBasketItem
  ): boolean => {
    if (this.settings.isLovenseBadlyConfigured(item)) {
      Emitter.emit(ShowLovenseHelpDialog, item);
      return false;
    } else {
      const cmd = new LovenseCommand(
        import.meta.env.VITE_LOVENSE_API_KEY as string,
        this.settings.getControl().userid,
        item.lovenseToyId as string,
        getCountMinutes(item.expectedCount ?? 10, item.countType) * 60,
        "Preset",
        item.lovenseProfile ?? LovensePresetName.pulse
      );
      void pushLovense(cmd);
      this.showMessage({
        message: "Command sent to Toy",
        notificationType: NotificationType.ToyInfo,
      });
      this.settings.auditUser(
        new AuditMessage(
          AuditActionTypes.Task,
          item.name,
          "Command sent to Toy",
          TraceLevel.info
        )
      );
      this.startTaskTimer(item);
    }
    return true;
  };

  private startTask = (item: BaseItem) => {
    const taskBasketItem = item as TaskBasketItem;
    const activeRouletteRequest = this.settings.getRouletteRequest();
    if (
      taskBasketItem.creditTaskType === TaskType.roulette &&
      activeRouletteRequest?.open &&
      activeRouletteRequest.rouletteTask?.key === taskBasketItem.key
    ) {
      this.settings.auditUser(
        new AuditMessage(
          AuditActionTypes.Session,
          taskBasketItem.name,
          "Ignoring duplicate roulette completion/play while wheel dialog is open",
          TraceLevel.info
        )
      );
      return;
    }
    taskBasketItem.decrementTimesRemaining();
    this.settings.updateTaskState(taskBasketItem);
    const task = this.settings.getTask(item.key) ?? item as ITaskItem; // in case of task being dynamically generated and duplicated
    if (task && TaskItem.IsLinesTask(task) && !TaskBasketItem.NeedsRouletteToChooseOption(taskBasketItem, task)
    ) {
      this.handleLinesTask(
        task,
        taskBasketItem.selectedCondition as string,
        taskBasketItem.expectedCount as number
      );
    } else if (task && taskBasketItem.creditTaskType && TaskItem.IsValidRouletteTask(taskBasketItem)) {
      const previewChoice = this.settings.getPreviewChoicesForItem(taskBasketItem);
      if (previewChoice && previewChoice.length > 0) {
        const request: IRouletteRequest = {
          rouletteTask: taskBasketItem,
          itemType: taskBasketItem.itemType,
          open: false,
          options: undefined,
          title: taskBasketItem.name,
          description: taskBasketItem.description
        };
        this.handleRouletteResult(previewChoice[0] as ITaskItem | IPenaltyItem, request);
      } else if (TaskItem.HasMoreThanOneChoiceValidOnRoulette(taskBasketItem)) {
        // Use the live basket item, not the static settings definition, so
        // runtime roulette state (e.g. expectedCount for multi-spin cycles)
        // is preserved in the dialog request.
        this.handleRouletteTask(taskBasketItem as unknown as ITaskItem);
      } else {
        let item: string | ITaskItem | IPenaltyItem | ITaskVoteConfig | undefined;
        const request: IRouletteRequest = {
          rouletteTask: taskBasketItem,
          itemType: taskBasketItem.itemType,
          open: false,
          options: undefined,
          title: taskBasketItem.name,
          description: taskBasketItem.description
        };
        const options: ItemAdditionOptions = {};
        if (task.taskKeys && task.taskKeys.length > 0) item = this.settings.getTask(task.taskKeys[0]);
        if (task.taskVotes && task.taskVotes.length > 0) {
          item = task.taskVotes[0];
          options.isVote = true;
        }
        if (task.penaltyKeys && task.penaltyKeys.length > 0) item = this.settings.getPenalty(task.penaltyKeys[0]);
        if (task.pilloryKeys && task.pilloryKeys.length > 0) {
          item = this.settings.getPenalty(task.pilloryKeys[0]);
          options.isPillory = true;
        }
        if (task.options && task.options.length > 0) {
          request.options = task.options;
          item = task.options[0];
        }
        if (!item) return;
        this.handleRouletteResult(item, request, options);
      }
    } else if (TaskBasketItem.NeedsRouletteToChooseOption(taskBasketItem, task as IOptionSupport)) {
      this.handleRouletteTask(taskBasketItem, (task as IOptionSupport).options);
      return; // leave the item in the basket for now
    } else if (isTimedItem(taskBasketItem.countType) || TaskItem.HasEndTriggerEvent(taskBasketItem)) {
      this.startTaskTimer(taskBasketItem);
    } else {
      this.onTaskCompleted(taskBasketItem.key);
    }

    if (
      taskBasketItem.creditTaskType === TaskType.acknowledgement &&
      taskBasketItem.itemType === ItemTypes.event
    ) {
      // the credit Task item key is the event key
      this.triggerItemsAwaitingEvent(taskBasketItem.key, true);
    }
  };

  private startPenalty = (item: BaseItem) => {
    const penaltyBasketItem = item as PenaltyBasketItem;
    const penalty = this.settings.getPenalty(item.key) ?? item as IPenaltyItem; // in case of penalty being dynamically generated and duplicated
    if (penalty && PenaltyItem.IsLinesPenalty(penalty)) {
      this.handleLinesTask(
        penalty,
        penaltyBasketItem.selectedCondition as string,
        penaltyBasketItem.expectedCount as number
      );
    } else if (
      penalty &&
      PenaltyItem.IsPenaltyTask(penaltyBasketItem as IPenaltyItem) &&
      TaskBasketItem.NeedsRouletteToChooseOption(penaltyBasketItem, penalty)
    ) {
      this.handleRouletteTask(penalty, penalty.options);
      return;
    } else if (
      penalty &&
      PenaltyItem.IsPenaltyTask(penaltyBasketItem as IPenaltyItem) &&
      (isTimedItem(penaltyBasketItem.countType) ||
        PenaltyItem.HasEndTriggerEvent(penalty))
    ) {
      this.startTaskTimer(penaltyBasketItem);
    } else {
      this.completePenaltyItem(penaltyBasketItem);
    }
  };

  private startLinkedCustomEventTasks = (parentItem: TaskBasketItem): void => {
    if (parentItem.itemType !== ItemTypes.event) return;
    if (parentItem.creditTaskType !== TaskType.acknowledgement) return;
    if (parentItem.monitoredTimer !== true) return;

    const linkedTasks = Object.values(this.settings.getTaskSchedule()).filter((task) => {
      if (!task || task.key === parentItem.key) return false;
      if (!task.isPaused || task.isComplete) return false;
      const sameRequester = task.requestedByKey === parentItem.key;
      const sameCustomSchedule = !!task.customScheduleKey
        && !!parentItem.customScheduleKey
        && task.customScheduleKey === parentItem.customScheduleKey;
      return task.autoStartOnRequestedByStart === true && (sameRequester || sameCustomSchedule);
    });

    linkedTasks.forEach((task) => {
      task.autoStartedByRequestedBy = true;
      this.onPlayItem(task as unknown as BaseItem);
    });
  };

  /* EVENT HANDLERS */
  // handles when the user moves an item from saved for later to in task list
  public onPlayItem = (item: BaseItem): void => {
    try {
      const hydratedItem = item.itemType === ItemTypes.task && !(item instanceof TaskBasketItem)
        ? new TaskBasketItem(item as unknown as ITaskTimerInfo)
        : item.itemType === ItemTypes.penalty && !(item instanceof PenaltyBasketItem)
          ? new PenaltyBasketItem(item as unknown as IPenaltyTimerInfo)
          : item;

      this.settings.updateLastRunDate(hydratedItem);
      if (hydratedItem.itemType === ItemTypes.position) {
        if ((hydratedItem as PositionBasketItem).hasMetronome) this.handleMetronomeStart();
        else if ((hydratedItem as PositionBasketItem).hasSiteEffects) this.handleSiteEffectsStart();
        else this.handlePositionStart();
      } else if (
        (hydratedItem instanceof PenaltyBasketItem || hydratedItem instanceof TaskBasketItem) &&
        hydratedItem.isLovenseToy &&
        isTimedItem(hydratedItem.countType)
      ) {
        if (!this.startLovenseTask(hydratedItem)) return;
      } else if (
        hydratedItem.itemType === ItemTypes.task ||
        (hydratedItem as TaskBasketItem).creditTaskType === TaskType.acknowledgement
      ) {
        this.startTask(hydratedItem);
        if (hydratedItem instanceof TaskBasketItem) {
          this.startLinkedCustomEventTasks(hydratedItem);
        }
      } else if (hydratedItem.itemType === ItemTypes.penalty) {
        this.startPenalty(hydratedItem);
      }
      if (
        (hydratedItem instanceof PenaltyBasketItem || hydratedItem instanceof TaskBasketItem) &&
        TaskItem.IsLovenseVote(hydratedItem)
      ) {
        this.settings.testToy(hydratedItem.lovenseToyId as string);
      }
      void this.handleTaskActionsValidation(hydratedItem);
      this.handleProofRequest(hydratedItem as TaskBasketItem | PenaltyBasketItem);
      this.settings.fireListeners();
    } catch (error) {
      const e = error instanceof Error ? error : new Error(String(error));
      sendExceptionMessage(this.getSettings(), e);
      this.settings.auditUser(
        new AuditMessage(
          AuditActionTypes.Exception,
          "on handle play item exception",
          e.message,
          TraceLevel.error
        )
      );
    }
  };

  private handleTaskActionsValidation = async (
    item: BaseItem
  ): Promise<void> => {
    if (
      item.itemType === ItemTypes.task ||
      item.itemType === ItemTypes.penalty ||
      item.itemType === ItemTypes.position
    ) {
      const isTimed = isTimedItem((item as ITimedBasketInfo).countType);
      // determine if this task has been started within the last minute of the last task
      if (
        (item instanceof PenaltyBasketItem &&
          item.penaltyType !== PenaltyType.acknowledgement) ||
        (item instanceof TaskBasketItem &&
          item.creditTaskType !== TaskType.acknowledgement)
      )
        await handleStartingTask(
          this.settings,
          item,
          this.justAddedTask,
          isTimed
        );
      if (isTimed) {
        this.justAddedTask = true;
        setTimeout(() => (this.justAddedTask = false), 60000);
      }
    }
  };

  private handleProofRequest = (
    item: TaskBasketItem | PenaltyBasketItem
  ): void => {
    this.taskManager.handleProofRequest(item);
  };

  private addPermanentTask = (
    itemToAdd: TaskItem,
    taskSchedule: ITaskBasketInfo
  ): void => {
    this.taskManager.addPermanentTask(itemToAdd, taskSchedule);
  };

  private completePenaltyItem = (basketItem: PenaltyBasketItem): void => {
    this.penaltyManager.completePenaltyItem(basketItem);
    this.settings.fireListeners();
  };

  // moves the item back to saved for later if allowed
  private handleCreditItemReset = (item: ISettingsItem): void => {
    this.taskManager.handleCreditItemReset(item);
  };

  // queues up a request until the system decides to answer
  public addPermissionToSchedule = (
    item: PermissionItem,
    scheduleReason: string
  ): void => {
    this.eventProcessor.addPermissionToSchedule(item, scheduleReason);
  };

  // queues up a request until the system decides to answer
  public addEventToSchedule = (
    item: EventItem,
    scheduleReason: string
  ): void => {
    this.eventProcessor.addEventToSchedule(item, scheduleReason);
  };

  // queues up a request until the system decides to answer
  private scheduleAddTaskToBasket = (
    item: TaskItem,
    scheduleReason: string
  ): void => {
    this.eventScheduleManager.scheduleAddTaskToBasket(item, scheduleReason);
  };

  // so we have the basket items to add into the schedule
  // but if it's a require proof task then we can play the task immeditately so they don't see it to start
  // how to work out if the task is just a request for proof
  private handleAutoProofRequest = (items: (TaskBasketItem | PenaltyBasketItem)[] | undefined): void => {
    if (items) {
      items.forEach(basketItem => {
        if (TaskItem.IsProofRequestTask(basketItem)
          // this determines that this task has nothing special set up or needed
          && !basketItem.timerTicks // the task won't have a count as it's a basket item so the timer ticks would be defined instead
          && (basketItem.selectedItems === undefined || Object.keys(basketItem.selectedItems).length === 0)) {
          // force the proof request immediately
          this.settings.auditUser(
            new AuditMessage(
              AuditActionTypes.Task,
              basketItem.name,
              "Auto Proof could be requested",
              TraceLevel.info, true)
          );
          this.onPlayItem(basketItem);
        }
      });
    }
  }

  //issue 1 gym bun added as something for auto proof
  //issue 2 a stacked up list of proofs are not processed one after the other

  // called from permission response notification screen when the response is not randomly generated
  private triggerItemsAwaitingEvent = (
    eventKey: string,
    notifyUI = true
  ): void => {
    const outstandingCreditTriggers =
      this.settings.getEventTriggeredBasketTasks();
    const matches =
      outstandingCreditTriggers &&
      outstandingCreditTriggers.filter(
        (cbi) => cbi?.endTriggerEvent === eventKey
      );
    if (matches) {
      for (let i = 0; i < matches.length; i++) {
        const item = matches[i] as BaseBasketItem;
        if (item?.itemType === ItemTypes.task) {
          this.onTaskCompleted(item.key, notifyUI);
          if (notifyUI) {
            if (this.settings.getIsSoundEnabled()) ring.play();
            Emitter.emitNotification({
              message: `Task Complete On ${this.settings.getEvent(eventKey)?.name
                }`,
              success: true,
              notificationType: NotificationType.ScheduleInfo,
            });
          }
        } else if (item?.itemType === ItemTypes.penalty) {
          this.completePenaltyItem(item as PenaltyBasketItem);
          if (notifyUI) {
            if (this.settings.getIsSoundEnabled()) ring.play();
            Emitter.emitNotification({
              message: `Penalty Complete On ${this.settings.getEvent(eventKey)?.name
                }`,
              success: true,
              notificationType: NotificationType.ScheduleInfo,
            });
          }
        }
      }
    }
  };

  // add an item to the task list and process it based on the type
  public onAddToSchedule = (
    item: ISettingsItem,
    scheduleReason: string,
    options?: ItemAdditionOptions
  ): void => {
    if (item.itemType === ItemTypes.task)
      this.settings.addTaskToSchedule(
        item as TaskItem,
        scheduleReason,
        undefined,
        options
      );
    else if (item.itemType === ItemTypes.penalty)
      this.settings.addPenaltyToSchedule(
        item as PenaltyItem,
        scheduleReason,
        undefined,
        options
      );
    else if (item.itemType === ItemTypes.permission)
      this.addPermissionToSchedule(item as PermissionItem, scheduleReason);
    else if (item.itemType === ItemTypes.position)
      this.positionTracker.addPositionToSchedule(item as PositionItem, 1); // this is in seconds
    else this.addEventToSchedule(item as EventItem, scheduleReason);
  };

  // handles when the user starts a position
  private handlePositionStart = (): void => {
    const positionSettings = this.settings.getPositionSettings();
    const positionInfo = this.settings.getPositionInfo();
    const now = new Date();
    const duration =
      60 *
      getDecimalRandom(
        positionSettings.positionLowerDuration,
        positionSettings.positionUpperDuration
      );
    const endTime = addSeconds(now, duration);
    const newTicks = differenceInSeconds(endTime, now);
    positionInfo.duration = duration;
    positionInfo.endTime = endTime;
    positionInfo.isPaused = false;
    positionInfo.isComplete = false;
    this.settings.updatePositionState(positionInfo);
    this.cookies.setState(COOKIE_KEY_POSITION_TICKS, newTicks.toString());
    this.positionTracker.startPositionTimer();
    this.settings.auditUser(
      new AuditMessage(
        AuditActionTypes.Position,
        positionInfo.name,
        "Time Started - " + convertToMinutes(duration),
        TraceLevel.info
      )
    );
  };

  // sets up a daily report dialog - extra one if set for twice
  private handleMetronomeStart = (): void => {
    const metronomeInfo = this.settings.getMetronomeInfo();
    if (!metronomeInfo?.hasMetronome || metronomeInfo.expectedCount <= 0) return;

    if (!metronomeInfo.isPaused && !metronomeInfo.isComplete) {
      return;
    }

    const now = new Date();
    const duration = metronomeInfo.duration ?? 60 * getRandom(1, 5);
    const intervalMs = metronomeInfo.intervalMs ?? getRandom(2, 20) * 1000;
    metronomeInfo.duration = duration;
    metronomeInfo.intervalMs = intervalMs;
    metronomeInfo.endTime = addSeconds(now, duration);
    metronomeInfo.startedTime = now;
    metronomeInfo.isPaused = false;
    metronomeInfo.isComplete = false;
    this.settings.updateMetronomeState(metronomeInfo);
    this.settings.auditUser(
      new AuditMessage(
        AuditActionTypes.Task,
        metronomeInfo.name,
        "Metronome started",
        TraceLevel.info
      )
    );
  };

  private handleMetronomeComplete = (): void => {
    const metronomeInfo = this.settings.getMetronomeInfo();
    if (!metronomeInfo?.hasMetronome || metronomeInfo.isComplete || metronomeInfo.isPaused) return;

    delete metronomeInfo.endTime;
    metronomeInfo.expectedCount = Math.max(0, (metronomeInfo.expectedCount ?? 1) - 1);

    Emitter.emit(
      METRONOME_TIMER_COMPLETE,
      new PositionBasketItem(
        {
          ...metronomeInfo,
          isComplete: metronomeInfo.expectedCount <= 0,
          completedTime: new Date(),
          isPaused: false,
        } as IPositionBasketInfo,
        0
      )
    );

    if (this.settings.getIsSoundEnabled()) ring.play();

    delete metronomeInfo.duration;
    delete metronomeInfo.intervalMs;

    if (metronomeInfo.expectedCount > 0) {
      metronomeInfo.isPaused = true;
      metronomeInfo.isComplete = false;
      this.settings.updateMetronomeState(metronomeInfo);
      this.settings.auditUser(
        new AuditMessage(
          AuditActionTypes.Task,
          metronomeInfo.name,
          "Metronome partial complete",
          TraceLevel.info
        )
      );
      Emitter.emitNotification({
        message: `Metronome complete — ${metronomeInfo.expectedCount} remaining today`,
        success: true,
        notificationType: NotificationType.ScheduleInfo,
      });
    } else {
      metronomeInfo.isPaused = false;
      metronomeInfo.isComplete = true;
      metronomeInfo.completedTime = new Date();
      this.settings.updateMetronomeState(metronomeInfo);
      this.settings.auditUser(
        new AuditMessage(
          AuditActionTypes.Task,
          metronomeInfo.name,
          "Metronome complete",
          TraceLevel.info
        )
      );
      Emitter.emitNotification({
        message: "Metronome complete for today",
        success: true,
        notificationType: NotificationType.ScheduleInfo,
      });
    }

    this.settings.fireListeners();
  };

  private handleMetronomeTick = (): void => {
    const metronomeInfo = this.settings.getMetronomeInfo();
    if (
      metronomeInfo?.hasMetronome &&
      metronomeInfo.endTime &&
      new Date(metronomeInfo.endTime) < new Date() &&
      !metronomeInfo.isPaused &&
      !metronomeInfo.isComplete
    ) {
      this.handleMetronomeComplete();
    }
  };

  private handleSiteEffectsStart = (): void => {
    const siteEffectsInfo = this.settings.getSiteEffectsInfo();
    if (!siteEffectsInfo?.hasSiteEffects || siteEffectsInfo.expectedCount <= 0) return;
    if (!siteEffectsInfo.isPaused && !siteEffectsInfo.isComplete) {
      return;
    }

    const now = new Date();
    const duration = siteEffectsInfo.duration ?? 60;
    siteEffectsInfo.duration = duration;
    siteEffectsInfo.endTime = addSeconds(now, duration);
    siteEffectsInfo.startedTime = now;
    siteEffectsInfo.isPaused = false;
    siteEffectsInfo.isComplete = false;
    this.settings.updateSiteEffectsState(siteEffectsInfo);

    void this.emitResolvedSiteEffectsOverlay(
      siteEffectsInfo.name,
      siteEffectsInfo.siteEffectsSettings,
      duration
    );
  };

  private emitResolvedSiteEffectsOverlay = async (
    title: string,
    siteEffectsSettings: ISiteEffectsSettings | undefined,
    durationSec: number
  ): Promise<void> => {
    let resolvedSettings = siteEffectsSettings;
    const selectedCategories = Array.from(
      new Set((siteEffectsSettings?.siteFxCategories ?? []).filter((c) => !!c).map((c) => c.trim()).filter((c) => !!c))
    );

    const aggregateItems = (results: Array<{ items: { image: string[]; video: string[]; audio: string[] } } | undefined>) => ({
      image: Array.from(new Set(results.flatMap((r) => r?.items.image ?? []))),
      video: Array.from(new Set(results.flatMap((r) => r?.items.video ?? []))),
      audio: Array.from(new Set(results.flatMap((r) => r?.items.audio ?? []))),
    });
    const hasAnyItems = (items: { image: string[]; video: string[]; audio: string[] }) =>
      items.image.length > 0 || items.video.length > 0 || items.audio.length > 0;
    const hasAllMediaTypes = (types: string[] | undefined) => {
      const set = new Set((types ?? []).map((t) => t.toLowerCase()));
      return set.has("image") && set.has("video") && set.has("audio");
    };
    const findFirstAllMediaCategory = (
      results: Array<
        | {
          availableCategories?: string[];
          categoryMediaTypes?: Record<string, string[]>;
        }
        | undefined
      >
    ): string | undefined => {
      for (const result of results) {
        if (!result) continue;
        const available = result.availableCategories ?? [];
        const mediaTypesByCategory = result.categoryMediaTypes ?? {};
        for (const category of available) {
          const exactTypes = mediaTypesByCategory[category];
          const normalizedTypes = mediaTypesByCategory[category.trim().toLowerCase()];
          if (hasAllMediaTypes(exactTypes ?? normalizedTypes)) {
            return category;
          }
        }
      }
      return undefined;
    };

    if (siteEffectsSettings) {
      const categoriesToResolve = selectedCategories.includes("all")
        ? ["all"]
        : selectedCategories;

      // Resolve selected categories and aggregate.
      let results = await Promise.all(
        (categoriesToResolve.length > 0 ? categoriesToResolve : ["all"]).map((cat) =>
          resolveSiteFxMedia({
            category: cat,
            mediaTypes: ["image", "video", "audio"],
          })
        )
      );
      let aggregated = aggregateItems(results as Array<{ items: { image: string[]; video: string[]; audio: string[] } } | undefined>);

      // If nothing is selected or selected categories resolve to no media, fallback to
      // the first available category that has image+video+audio.
      if (categoriesToResolve.length === 0 || !hasAnyItems(aggregated)) {
        const discovery = await resolveSiteFxMedia({
          category: "all",
          mediaTypes: ["image", "video", "audio"],
        });
        const fallbackCategory = findFirstAllMediaCategory([discovery]);
        if (fallbackCategory) {
          results = await Promise.all([
            resolveSiteFxMedia({
              category: fallbackCategory,
              mediaTypes: ["image", "video", "audio"],
            }),
          ]);
          const fallbackAggregated = aggregateItems(results as Array<{ items: { image: string[]; video: string[]; audio: string[] } } | undefined>);
          if (hasAnyItems(fallbackAggregated)) {
            aggregated = fallbackAggregated;
          }
        }
      }

      if (results && results.length > 0) {
        const merged = cloneObject(siteEffectsSettings) as ISiteEffectsSettings;
        if (aggregated.image.length > 0) {
          merged.images.urls = aggregated.image;
        }
        if (aggregated.video.length > 0) {
          merged.videos.urls = aggregated.video;
        }
        if (aggregated.audio.length > 0) {
          merged.audio.urls = aggregated.audio;
        }
        resolvedSettings = merged;
      }
    }

    Emitter.emit(ShowSiteEffectsOverlay, {
      title,
      config: resolvedSettings,
      durationSec,
      ownerTest: false,
      onComplete: () => this.handleSiteEffectsComplete(),
    });
  };

  public handleSiteEffectsComplete = (): void => {
    const siteEffectsInfo = this.settings.getSiteEffectsInfo();
    if (!siteEffectsInfo?.hasSiteEffects || siteEffectsInfo.isComplete || siteEffectsInfo.isPaused) return;

    delete siteEffectsInfo.endTime;
    siteEffectsInfo.expectedCount = Math.max(0, (siteEffectsInfo.expectedCount ?? 1) - 1);

    Emitter.emit(
      SITE_EFFECTS_TIMER_COMPLETE,
      new PositionBasketItem(
        {
          ...siteEffectsInfo,
          isComplete: siteEffectsInfo.expectedCount <= 0,
          completedTime: new Date(),
          isPaused: false,
        } as IPositionBasketInfo,
        0
      )
    );

    if (this.settings.getIsSoundEnabled()) ring.play();

    if (siteEffectsInfo.expectedCount > 0) {
      siteEffectsInfo.isPaused = true;
      siteEffectsInfo.isComplete = false;
      this.settings.updateSiteEffectsState(siteEffectsInfo);
      Emitter.emitNotification({
        message: `Site FX complete — ${siteEffectsInfo.expectedCount} remaining today`,
        success: true,
        notificationType: NotificationType.ScheduleInfo,
      });
    } else {
      siteEffectsInfo.isPaused = false;
      siteEffectsInfo.isComplete = true;
      siteEffectsInfo.completedTime = new Date();
      this.settings.updateSiteEffectsState(siteEffectsInfo);
      Emitter.emitNotification({
        message: "Site FX complete for today",
        success: true,
        notificationType: NotificationType.ScheduleInfo,
      });
    }

    this.settings.fireListeners();
  };

  private handleSiteEffectsTick = (): void => {
    const siteEffectsInfo = this.settings.getSiteEffectsInfo();
    if (
      siteEffectsInfo?.hasSiteEffects &&
      siteEffectsInfo.endTime &&
      new Date(siteEffectsInfo.endTime) < new Date() &&
      !siteEffectsInfo.isPaused &&
      !siteEffectsInfo.isComplete
    ) {
      this.handleSiteEffectsComplete();
    }
  };

  private getDailyReportScheduleConfig = () => {
    const generalSettings = this.settings.getGeneralSettings();
    const today = new Date();
    const dateDay = convertToEventDayFromDate(today.getDay());
    const customSchedule = this.settings.getScheduledCustomSchedule(dateDay, today);
    const hasCustomReportOverride = Boolean(
      customSchedule
      && (
        customSchedule.dailyReporting !== undefined
        || customSchedule.minReportTime !== undefined
        || customSchedule.maxReportTime !== undefined
        || customSchedule.reportStyle !== undefined
        || customSchedule.reportWordCount !== undefined
      )
    );

    if (!hasCustomReportOverride) {
      return {
        dailyReporting: generalSettings.dailyReporting,
        minReportTime: generalSettings.minReportTime,
        maxReportTime: generalSettings.maxReportTime,
        reportStyle: generalSettings.reportStyle,
        reportWordCount: generalSettings.reportWordCount,
        sourceDescription: "General Settings",
      };
    }

    return {
      // If a custom schedule provides report-specific overrides but does not
      // explicitly set dailyReporting, treat reporting as enabled for that day.
      dailyReporting: customSchedule?.dailyReporting ?? true,
      minReportTime: customSchedule?.minReportTime ?? generalSettings.minReportTime,
      maxReportTime: customSchedule?.maxReportTime ?? generalSettings.maxReportTime,
      reportStyle: customSchedule?.reportStyle ?? generalSettings.reportStyle,
      reportWordCount: customSchedule?.reportWordCount ?? generalSettings.reportWordCount,
      sourceDescription: `Custom Schedule (${customSchedule?.name ?? "Unnamed"})`,
    };
  };

  private setupMorningReport = (): void => {
    const reportConfig = this.getDailyReportScheduleConfig();
    if (
      !reportConfig.dailyReporting ||
      !this.settings.canPerformSomeTasksForToday() ||
      reportConfig.reportStyle === ReportStyle.once
    )
      return;
    scheduleEvent(
      this.settings,
      {
        name: "Morning Report",
        minTime: "04:00",
        maxTime: "09:00",
        img: "report",
        imgId: "",
        availability: AvailabilityType.daily,
        approvalChance: 1,
        hasOptions: false,
        optionAppliedChance: 1,
        key: MORNING_REPORT_ID,
        description: "Morning Report Request",
        itemType: ItemTypes.event,
        eventType: EventType.standard,
        disableAdd: false,
        availabilityDayType: AvailabilityDayType.AllDays,
      },
      `Reports are scheduled based on the Report settings in ${reportConfig.sourceDescription}. When a report is required twice a day, the morning report is added at a random time between 4am and 9am`,
      new Date(),
      { morningReport: true, forceSchedule: true }
    );
  };

  // sets up a daily report dialog
  private setUpDailyReport = (): void => {
    const reportConfig = this.getDailyReportScheduleConfig();
    if (
      !reportConfig.dailyReporting ||
      !this.settings.canPerformSomeTasksForToday()
    )
      return;
    const setupOK = scheduleEvent(
      this.settings,
      {
        name: "Daily Report",
        minTime: reportConfig.minReportTime,
        maxTime: reportConfig.maxReportTime,
        img: "report",
        imgId: "",
        availability: AvailabilityType.daily,
        approvalChance: 1,
        hasOptions: false,
        optionAppliedChance: 1,
        key: DAILY_REPORT_ID,
        description: "Daily Report Request",
        itemType: ItemTypes.event,
        eventType: EventType.standard,
        disableAdd: false,
        availabilityDayType: AvailabilityDayType.AllDays,
      },
      `Reports are scheduled based on the Report settings in ${reportConfig.sourceDescription}. When a report is required, the evening report is added at a time between min and max report times.`,
      new Date(),
      { dailyReport: true }
    );
    this.settings.updateReportComplete(!setupOK); // if unable to add (in past) then don't force a review today
  };

  // when a minute timer completes, here's what we check
  private handleMinuteTick = async (): Promise<void> => {
    if (this.isDisposed) return;
    if (
      this.state === SchedulerState.Uninitialised ||
      this.state === SchedulerState.InitialisingDay ||
      this.state === SchedulerState.RefreshingTasks ||
      !this.settings.isLoaded() ||
      this.state === SchedulerState.RollingDay ||
      !this.settings.getControl().userid
    )
      return;

    const previousState = this.state;
    this.state = SchedulerState.RefreshingTasks;
    try {
      let newDay = await this.checkIfNewDay();
      newDay = (await this.checkScheduleForComplete()) || newDay;
      this.checkRunningVotes();
      this.handleMetronomeTick();
      this.handleSiteEffectsTick();
      if (!newDay) {
        this.refreshTasks();
        this.handlePendingItems(); // these can be added intra day
        this.handleEnforcedTasks(true).catch((error) => {
          console.error("Failed to handle enforced tasks:", error);
          this.settings.auditUser(
            new AuditMessage(
              AuditActionTypes.Task,
              "Enforced Tasks",
              `Failed to handle enforced tasks: ${error instanceof Error ? error.message : String(error)}`,
              TraceLevel.error
            )
          );
        });
      }
    } finally {
      this.state = previousState;
    }
  };

  private checkRunningVotes = (): void => {
    this.settings.checkCanUnlockSession();
    this.handleCompletedPillories(false);
    this.checkForCompleteTaskVotes(false);
    this.checkForTaskReceiptUnlocks(false);
  };

  private canFireEvent = (scheduleItem: ISchedulerTimerInfo): boolean => {
    return this.eventScheduleManager.canFireEvent(scheduleItem);
  };

  private removeItemFromSchedule = (scheduleItemKey: string): void => {
    this.eventScheduleManager.removeItemFromSchedule(scheduleItemKey);
  };

  // on timer tick, check and fire completed scheduler entries that are in the past
  // returns if it fired off a new day initialisation
  public checkScheduleForComplete = async (): Promise<boolean> => {
    return this.eventScheduleManager.checkScheduleForComplete();
  };

  private onScheduleItemComplete = (
    scheduleItem: ISchedulerTimerInfo
  ): void => {
    this.eventScheduleManager.onScheduleItemComplete(scheduleItem);
  };

  private handleMissedScheduleItem = (
    reportTime: Date,
    item: {
      name: string;
      missEventPenalties?: string[];
      missEventPillories?: string[];
    },
    basketItem: PermissionBasketItem | EventBasketItem | undefined
  ) => {
    if (!basketItem) return;
    if (!basketItem.decision) return;
    const mins = differenceInMinutes(reportTime, new Date());
    if (mins < -2) {
      // this was missed
      if (item && item.missEventPenalties) {
        item.missEventPenalties?.forEach((key) => {
          const pen = this.settings.getPenalty(key);
          if (pen)
            this.settings.addPenaltyToSchedule(
              pen,
              `Missed ${item.name} that was pending, should be more dutiful`,
              undefined,
              {
                reason: `Missed ${item.name} that was pending that was due whilst logged out, should be more dutiful`,
              }
            );
        });
      }
      if (item && item.missEventPillories) {
        item.missEventPillories?.forEach((key) => {
          const pen = this.settings.getPenalty(key);
          if (!pen) return;
          const defaults = getPilloryTopicDefaults(pen);
          this.settings.createPilloryTopic({
            addCountLimits: false,
            countType: defaults.countType,
            customCondition: false,
            date: addDays(new Date(), 1),
            item: pen,
            reason: `Missed ${item.name} that was pending that was due whilst logged out, should be more dutiful`,
            expectedCount: defaults.count,
            isLovenseToy: pen.isLovenseToy,
            lovenseProfile: pen.lovenseProfile,
            lovenseToyId: pen.lovenseToyId,
            votable: true,
            voteCount: defaults.count,
          });
        });
      }
    }
  };

  public onEventComplete = (
    item: IEventItem,
    overrideDecision?: boolean,
    reason?: string,
    ignoreMessage?: boolean,
    bypassAdditionChecks?: boolean
  ): EventBasketItem | undefined => {
    return this.eventProcessor.onEventComplete(
      item,
      overrideDecision,
      reason,
      ignoreMessage,
      bypassAdditionChecks
    );
  };

  // handle adding permission to task list when the scheduler responds
  private onPermissionRequested = (
    itemKey: string,
    overrideDecision?: boolean,
    additionalInfo?: IEventCustomDecisionResponse
  ): PermissionBasketItem | undefined => {
    return this.eventProcessor.onPermissionRequested(itemKey, overrideDecision, additionalInfo);
  };

  private addPermissionToBasket = (
    item: string
  ): PermissionBasketItem | undefined => {
    if (!item) return;
    return this.eventProcessor.addPermissionToBasket(item);
  };

  private handleMorningReportRequest = (): void => {
    const generalSettings = this.settings.getGeneralSettings();
    const reportConfig = this.getDailyReportScheduleConfig();
    let wordCount = reportConfig.reportWordCount;
    const isWeekendDay = this.settings.getIsWeekend();
    if (
      isWeekendDay &&
      generalSettings.weekendHandling === WeekendHandling.harsh
    )
      wordCount = wordCount * 1.5;
    else if (
      isWeekendDay &&
      generalSettings.weekendHandling === WeekendHandling.easy
    )
      wordCount = wordCount * 0.5;
    const count = getRandom(wordCount * 0.75, wordCount * 1.25);
    this.settings.updateMorningReportDialog({
      count,
      open: true,
    });
  };

  private handleScheduledAttendance = (scheduleItem: ISchedulerTimerInfo): void => {
    const attendanceOptions = scheduleItem.scheduleOptions?.customAttendance;
    if (!attendanceOptions) return;
    if (!this.settings.canPerformSomeTasksForToday()) return;
    if (this.settings.getRequestAttendance()) {
      this.settings.auditUser(
        new AuditMessage(
          AuditActionTypes.Session,
          "Attendance Request",
          "Skipped automated attendance because another attendance request is already active",
          TraceLevel.warning
        )
      );
      return;
    }

    const control = this.settings.getControl();
    if (control.isOwnedBySelf()) return;

    const positions = this.settings.getPositions(
      (position) => !position.hidden && !position.isDeleted && !position.isNew && this.settings.canAddTask(position).isOk
    );
    const allPenalties = this.settings.getPenalties(
      (penalty) => !penalty.hidden && !penalty.isDeleted && !penalty.isNew && this.settings.canAddTask(penalty).isOk
    );
    const configuredPenaltyKeys = attendanceOptions.penaltyKeys ?? [];
    const configuredPositionKeys = attendanceOptions.positionKeys ?? [];
    const filteredPositions = configuredPositionKeys.length > 0
      ? positions.filter((position) => configuredPositionKeys.includes(position.key))
      : positions;
    const penalties = configuredPenaltyKeys.length > 0
      ? allPenalties.filter((penalty) => configuredPenaltyKeys.includes(penalty.key))
      : allPenalties;
    if (filteredPositions.length === 0 || penalties.length === 0) {
      this.settings.auditUser(
        new AuditMessage(
          AuditActionTypes.Session,
          "Attendance Request",
          "Skipped automated attendance because no valid positions or penalties were available",
          TraceLevel.warning
        )
      );
      return;
    }

    const responseTimeoutSec = Math.max(15, attendanceOptions.responseTimeoutSec ?? 60);
    const holdMinSec = Math.max(60, attendanceOptions.holdMinMinutes * 60);
    const holdMaxSec = Math.max(holdMinSec, attendanceOptions.holdMaxMinutes * 60);
    const holdDurationSec = getRandom(holdMinSec, holdMaxSec);
    const position = filteredPositions[getRandom(0, filteredPositions.length - 1)];
    const penalty = penalties[getRandom(0, penalties.length - 1)];

    handleAttendanceRequest(this.settings, responseTimeoutSec);
    void this.settings.sendAttendanceRequestToMember(
      control,
      position,
      penalty,
      addSeconds(new Date(), responseTimeoutSec),
      holdDurationSec
    );
    this.settings.auditUser(
      new AuditMessage(
        AuditActionTypes.Session,
        "Attendance Request",
        `Automated attendance triggered for ${position.name} with ${responseTimeoutSec}s response window and ${holdDurationSec}s hold time`,
        TraceLevel.info
      )
    );
  };

  // on request for report, set the state which should force rerender and update of UI
  private handleLinesTask = (
    item: ITaskItem | IPenaltyItem,
    line: string,
    count: number
  ): void => {
    this.settings.updateLinesDialog({
      count,
      line,
      item,
    });
  };

  // on request for report, set the state which should force rerender and update of UI
  private handleRouletteTask = (
    item: ITaskItem | IPenaltyItem,
    options?: string[]
  ): void => {
    this.rouletteManager.handleRouletteTask(item, options);
  };

  // on request for report, set the state which should force rerender and update of UI
  private handleDailyReportRequest = (): void => {
    const generalSettings = this.settings.getGeneralSettings();
    const reportConfig = this.getDailyReportScheduleConfig();
    let wordCount = reportConfig.reportWordCount;
    const isWeekendDay = this.settings.getIsWeekend();
    if (
      isWeekendDay &&
      generalSettings.weekendHandling === WeekendHandling.harsh
    )
      wordCount = wordCount * 1.5;
    else if (
      isWeekendDay &&
      generalSettings.weekendHandling === WeekendHandling.easy
    )
      wordCount = wordCount * 0.5;
    const count = getRandom(wordCount * 0.75, wordCount * 1.25);
    this.settings.updateReportDialog({
      count,
      open: true,
    });
  };

  /**
   * Handles the removal of a task that was previously permanent but is no longer
   */
  private handlePermanentTaskRemoval = (
    task: ITaskItem,
    taskSchedule: ITaskBasketInfo
  ): void => {
    if (task.preSelect === PreselectType.daily) {
      // was perm and now not perm but has taken place today already, so treat as a completed item
      (taskSchedule[task.key] as ITaskTimerInfo).permanent = false;
      taskSchedule[task.key].isComplete = true;
      taskSchedule[task.key].completedTime = new Date();
      taskSchedule[task.key].isPaused = false;
      this.settings.updateTaskState(taskSchedule[task.key]);
    } else {
      // an item was permanent and now not so remove it as a permanent item
      this.settings.deleteTaskState(task.key);
    }
    this.settings.auditUser(
      new AuditMessage(
        AuditActionTypes.Task,
        task.name,
        "Permanent Task Removed",
        TraceLevel.info
      )
    );
  };

  /**
   * pushes into the saved for later or current Task items that are set up
   * @param intraDay do we only check for changes in permenant tasks or do we sweep through all tasks that could be applied today
   * @returns
   */
  private handleEnforcedTasks = (intraDay: boolean, customDay?: boolean): Promise<void> => {
    return this.taskSelectionManager.handleEnforcedTasks(intraDay, customDay);
  };

  /**
   * Peeks to determine if there is a request to show the unlock image for a user and
   * shows the image - when the user then affirms they have seen it we clear that image
   * @param overrideSOD should we process on roll as this is part of the roll process or is this from the minute timer during roll
   * @param taskKey optional key to check - if not passed will process all found
   * @returns
   */
  private checkForTaskReceiptUnlocks = (
    overrideSOD: boolean,
    taskKey?: string
  ) => {
    if (this.settings.pendingRoll()) return;
    // handle the completion of tasks that should now show an image on completion
    if (!overrideSOD && this.state === SchedulerState.RollingDay) return;
    const taskLocksReceipts = this.settings.getMyTaskLocks();
    const taskSchedule = this.settings.getTaskSchedule();
    if (!taskLocksReceipts || taskLocksReceipts.length === 0) return;
    if (taskKey) {
      const receipts = taskLocksReceipts.filter((t) => t.taskKey === taskKey);
      if (receipts)
        receipts.forEach((r) => {
          this.settings.sendUnlockRequest(r);
        });
    } else {
      Object.values(taskLocksReceipts).forEach((t) => {
        if (
          t &&
          !t.isComplete && // only show the image if not already handled
          (!taskSchedule[t.taskKey] || taskSchedule[t.taskKey].isComplete)
        ) {
          this.settings.sendUnlockRequest(t);
        }
      });
    }
    // check for any task locks already complete and awaiting to be shown
    this.settings.handleCompletedLockTasks();
  };

  /**
   * finds any pillories which have completed and turns them into tasks to show instead
   * @param overrideSOD should we process on roll as this is part of the roll process or is this from the minute timer during roll
   * @returns
   */
  private handleCompletedPillories = (overrideSOD: boolean): void => {
    if (this.settings.pendingRoll()) return;
    if (!overrideSOD && this.state === SchedulerState.RollingDay) return;
    if (!this.settings.canPerformSomeTasksForToday()) return;

    const pilloriesToComplete = this.settings.getPilloriesComplete();
    if (pilloriesToComplete.length === 0) return;
    this.settings.auditUser(
      new AuditMessage(
        AuditActionTypes.Task,
        "Pillories Complete",
        `Handling Pillories - ${pilloriesToComplete.length} Items`,
        TraceLevel.info
      )
    );
    for (let i = 0; i < pilloriesToComplete.length; i += 1) {
      const pillory = pilloriesToComplete[i];

      if (pillory.item && pillory.item.hidden) {
        this.settings.auditUser(
          new AuditMessage(
            AuditActionTypes.Task,
            "Pillory Error",
            `Pillory Task cannot complete as ${pillory.item.name} is hidden. Removing pillory result.`,
            TraceLevel.error
          )
        );
        continue;
      } else if (pillory.item && pillory.item.isDeleted) {
        this.settings.auditUser(
          new AuditMessage(
            AuditActionTypes.Task,
            "Pillory Error",
            `Pillory Task cannot complete as ${pillory.item.name} is deleted. Removing pillory result.`,
            TraceLevel.error
          )
        );
        continue;
      }

      if (!pillory.itemAdditionOptions) pillory.itemAdditionOptions = {};
      pillory.itemAdditionOptions.expectedCount = pillory.getCurrentCount();
      if (isTimedItem(pillory.countType))
        pillory.itemAdditionOptions.expectedCount = getCountMinutes(
          pillory.getCurrentCount(),
          pillory.countType
        );
      if (!pillory.itemAdditionOptions.selectedCondition) {
        if (
          pillory.voteOptions &&
          Object.keys(pillory.voteOptions).length > 0
        ) {
          let option = "";
          let max = 0;
          for (let i = 0; i < Object.keys(pillory.voteOptions).length; i += 1) {
            const key = Object.keys(pillory.voteOptions)[i];
            if (!option) option = key;
            if (max < pillory.voteOptions[key]) {
              max = pillory.voteOptions[key];
              option = Object.keys(pillory.voteOptions)[i];
            }
          }
          pillory.itemAdditionOptions.selectedCondition = option;
        }
      }
      this.addPendingItemToBasket(
        pillory,
        "This task was part of a pillory and was added to the session when the pillory completed. The pillory result was added together and the task checked to ensure it was enabled, not in member limits and allowed based on the members diary today"
      );
      sendPilloryCompleteMessageToOwner(this.settings, pillory);
    }
    this.settings.clearCompletePillories(pilloriesToComplete);
  };

  /**
   * checks to see if any vote tasks are completed and the vote now means the task is completed
   * @param overrideSOD should we process on roll as this is part of the roll process or is this from the minute timer during roll
   * @returns
   */
  private checkForCompleteTaskVotes = (overrideSOD?: boolean): void => {
    if (this.settings.pendingRoll()) return;
    if (!overrideSOD && this.state === SchedulerState.RollingDay) return;
    if (!this.settings.isLoaded()) return;
    if (this.settings.getControl().userid !== this.cookies.userid) return;

    this.votingManager.checkForCompleteTaskVotes();
  };

  /**
   * Filters pending items based on processing criteria
   */
  private filterPendingItems(
    pendingItems: IPendingItem[],
    handleRemovalsOnly: boolean,
    canPerform: boolean
  ): IPendingItem[] {
    return pendingItems.filter(
      (item) =>
        (item.updateType !== PendingUpdateType.scheduleChange && // never handle custom schedules intraday
          !handleRemovalsOnly &&
          item.updateType !== PendingUpdateType.add) ||
        (handleRemovalsOnly && item.updateType === PendingUpdateType.remove) ||
        (handleRemovalsOnly && item.updateType === PendingUpdateType.scheduleRemove) ||
        (!handleRemovalsOnly &&
          item.updateType === PendingUpdateType.add &&
          canPerform &&
          (item.fireImmediate || !item.date || isBefore(new Date(item.date), new Date()))) ||
        (!handleRemovalsOnly && item.updateType === PendingUpdateType.scheduleRemove) ||
        (!handleRemovalsOnly && item.updateType === PendingUpdateType.scheduleReschedule) ||
        (!handleRemovalsOnly && item.updateType === PendingUpdateType.scheduleItemUpdate)
    );
  }

  /**
   * Refreshes item data to ensure we have the latest version.
   * Schedule operation types carry scheduler-specific state (startTime, approval info, owner
   * edits etc.) that getCurrentItem() would silently replace with the original settings
   * definition, so we skip the refresh for those types.
   */
  private refreshPendingItemData(pendingItem: IPendingItem): void {
    if (
      pendingItem.updateType === PendingUpdateType.scheduleRemove ||
      pendingItem.updateType === PendingUpdateType.scheduleReschedule ||
      pendingItem.updateType === PendingUpdateType.scheduleItemUpdate
    ) return;
    if (pendingItem.item) {
      const latest = this.settings.getCurrentItem(pendingItem.item);
      if (latest) pendingItem.item = latest;
    }
  }

  /**
   * Handles preview creation request
   */
  private handleCreatePreview(pendingItem: IPendingItem): void {
    this.settings.auditUser(
      new AuditMessage(
        AuditActionTypes.Task,
        "Pending Items",
        `Handling Create Preview Request for ${pendingItem.item?.name}`,
        TraceLevel.info
      )
    );
    // Evict the stale detail cache for tomorrow's custom schedule before regenerating
    // the preview. The owner already sent a correct preview (Message #1) from their
    // session; when this handler runs on the member's scheduler the local detail cache
    // can still hold the pre-change snapshot (e.g. without the freshly-chosen outfit
    // option set in itemOptions). Clearing it forces createPreview to defer until a
    // fresh fetch from Firebase returns, guaranteeing the member's preview message
    // (Message #2) uses the same up-to-date data instead of a stale/random outfit.
    const tomorrowDate = addDays(new Date(), 1);
    const tomorrowDay = convertToEventDayFromDate(tomorrowDate.getDay());
    const tomorrowSchedule = this.settings.getScheduledCustomSchedule(tomorrowDay, tomorrowDate);
    if (tomorrowSchedule?.key) {
      this.settings.evictCustomScheduleDetailCache(tomorrowSchedule.key);
    }
    // Suppress the message here: the owner session already sent the correct preview
    // message (Message #1) via saveCustomSchedule → createPreview(true, false).
    // This handler runs on the member's scheduler to update their preview STATE only.
    // Passing suppressMessage=true prevents a duplicate "items required tomorrow" message.
    this.settings.createPreview(true, true);
  }

  /**
   * Handles proof request for a task or penalty
   */
  private handleProofRequestPending(pendingItem: IPendingItem): void {
    this.settings.auditUser(
      new AuditMessage(
        AuditActionTypes.Task,
        "Pending Items",
        `Handling Proof Request for ${pendingItem.item?.name}`,
        TraceLevel.info
      )
    );
    if (!pendingItem.item) return;
    const proofItem = pendingItem.item as
      | ITaskProofRequestItem
      | IPenaltyProofRequestItem;
    proofItem.proofCode = pendingItem.itemAdditionOptions?.reason as string;
    this.settings.updateProofDialog(proofItem, true);
  }

  /**
   * Handles early release of scheduled permission
   */
  private handlePermissionRelease(pendingItem: IPendingItem): void {
    this.settings.auditUser(
      new AuditMessage(
        AuditActionTypes.Task,
        "Pending Items",
        `Handling Early Schedule Release for ${pendingItem.item?.name}`,
        TraceLevel.info
      )
    );
    const info = this.settings.getScheduleInfo();
    const match = info.findIndex((i) => i.key === pendingItem.item?.key);
    if (match === -1) return;
    const schedule = info[match];
    schedule.startTime = new Date(); // set it to fire off now
    this.settings.updateSchedulerState(schedule);
  }

  /**
   * Handles permission decision change
   */
  private handlePermissionChange(pendingItem: IPendingItem): void {
    this.settings.auditUser(
      new AuditMessage(
        AuditActionTypes.Task,
        "Pending Items",
        `Handling Permission Decision Change for ${pendingItem.item?.name}`,
        TraceLevel.info
      )
    );
    const { customDecisionResponse } = pendingItem;
    if (!customDecisionResponse) return;

    // Remove existing permission state
    this.settings.deletePermissionState(customDecisionResponse.itemKey);

    // Remove all associated tasks
    const creditInfo = this.settings.getTaskSchedule();
    const keys = Object.keys(creditInfo);
    for (let i = 0; i < keys.length; i += 1) {
      const k = keys[i];
      if (k && k.startsWith(customDecisionResponse.itemKey)) {
        this.settings.deleteTaskState(k);
      }
    }

    // Apply new permission decision
    this.onPermissionRequested(
      customDecisionResponse.itemKey,
      customDecisionResponse.decision,
      customDecisionResponse
    );
  }

  /**
   * Handles removal of item from basket
   */
  private handleItemRemoval(pendingItem: IPendingItem): void {
    if (!pendingItem.item) return;
    this.settings.handleRemoveItemFromBasket(pendingItem.item);
    this.settings.auditUser(
      new AuditMessage(
        AuditActionTypes.Task,
        "Pending Items",
        `Admin Removed Item for ${pendingItem.item?.name}`,
        TraceLevel.info
      )
    );
  }

  /**
   * Handles update of existing item in basket
   */
  private handleItemUpdate(pendingItem: IPendingItem): void {
    if (!pendingItem.item) return;
    this.settings.handleUpdateBasket(
      pendingItem.item as TaskBasketItem | PenaltyBasketItem,
      pendingItem.itemAdditionOptions
    );
    this.settings.auditUser(
      new AuditMessage(
        AuditActionTypes.Task,
        "Pending Items",
        `Admin Updated Item for ${pendingItem.item?.name}`,
        TraceLevel.info
      )
    );
  }

  /**
   * Validates and fixes item type for pending additions
   */
  private validatePendingItemType(pendingItem: IPendingItem): boolean {
    if (!pendingItem.item) return false;

    if (
      pendingItem.item.itemType === undefined ||
      pendingItem.item.itemType === null
    ) {
      if (
        pendingItem.itemAdditionOptions &&
        pendingItem.itemAdditionOptions.isPillory
      ) {
        pendingItem.item.itemType = ItemTypes.penalty;
      } else {
        this.settings.auditUser(
          new AuditMessage(
            AuditActionTypes.Task,
            "Error finding pending item",
            "Unable to find pending item to add to scheduler as item type is not set",
            TraceLevel.error
          )
        );
        return false;
      }
    }

    if (!pendingItem.date) {
      this.settings.auditUser(
        new AuditMessage(
          AuditActionTypes.Task,
          "Date missing for pending add item",
          "Expected date to be specified for all add requests",
          TraceLevel.warning
        )
      );
    }

    return true;
  }

  /**
   * Handles addition of new item to basket
   */
  private handleItemAddition(pendingItem: IPendingItem): void {
    if (!pendingItem.item) return;

    this.settings.auditUser(
      new AuditMessage(
        AuditActionTypes.Task,
        "Pending Items",
        `Admin Added Item for ${pendingItem.item?.name}`,
        TraceLevel.info
      )
    );

    if (!this.validatePendingItemType(pendingItem)) return;

    const itemType = pendingItem.item.itemType;

    if (
      itemType === ItemTypes.task ||
      itemType === ItemTypes.penalty ||
      itemType === ItemTypes.position
    ) {
      this.addPendingItemToBasket(
        pendingItem,
        "This task was added to the session by the owner whom chose it and it was not limited by limits and was enabled and able to be selected"
      );
    } else if (itemType === ItemTypes.event) {
      this.addEventToSchedule(
        pendingItem.item as IEventItem,
        "This event was added to the session by the owner whom chose it and it was not limited by limits and was enabled and able to be selected"
      );
      this.settings.auditUser(
        new AuditMessage(
          AuditActionTypes.Event,
          pendingItem.item.name,
          "Owner Event Added Item",
          TraceLevel.info
        )
      );
    } else if (itemType === ItemTypes.permission) {
      const decision =
        pendingItem.itemAdditionOptions?.decision !== undefined
          ? pendingItem.itemAdditionOptions.decision
          : true;
      this.onPermissionRequested(
        (pendingItem.item as IPermissionItem).key,
        decision,
        {
          ...pendingItem.itemAdditionOptions,
          decision,
          itemKey: (pendingItem.item as IPermissionItem).key,
        }
      );
      this.settings.auditUser(
        new AuditMessage(
          AuditActionTypes.Permission,
          pendingItem.item.name,
          "Owner Permission Added Item",
          TraceLevel.info
        )
      );
    }
  }

  /**
   * Handles game scheduling request
   */
  private handleGameScheduling(pendingItem: IPendingItem): void {
    if (!pendingItem.gameRequest) return;
    this.settings.addScheduledGame(pendingItem.gameRequest);
    this.settings.auditUser(
      new AuditMessage(
        AuditActionTypes.Task,
        "Game Scheduled",
        `Owner Scheduled a Game - ${pendingItem.gameRequest.gameKey}`,
        TraceLevel.info
      )
    );
  }

  /**
   * Removes a pending schedule item (event/permission not yet fired) from the active schedule
   */
  private handleScheduleItemRemoval(pendingItem: IPendingItem): void {
    if (!pendingItem.item) return;
    this.removeItemFromSchedule(pendingItem.item.key);
    this.settings.auditUser(
      new AuditMessage(
        AuditActionTypes.Event,
        "Pending Items",
        `Owner Removed Scheduled Item: ${pendingItem.item.name}`,
        TraceLevel.info
      )
    );
  }

  /**
   * Reschedules a pending schedule item to a new start time
   */
  private handleScheduleItemReschedule(pendingItem: IPendingItem): void {
    if (!pendingItem.item) return;
    const info = this.settings.getScheduleInfo();
    const match = info.findIndex((i) => i.key === pendingItem.item?.key);
    if (match === -1) return;
    const schedule = info[match];
    if (pendingItem.date) {
      schedule.startTime = new Date(pendingItem.date);
    }
    this.settings.updateSchedulerState(schedule);
    this.settings.auditUser(
      new AuditMessage(
        AuditActionTypes.Event,
        "Pending Items",
        `Owner Rescheduled Item: ${pendingItem.item.name} to ${pendingItem.date}`,
        TraceLevel.info
      )
    );
  }

  /**
   * Updates the details of an existing pending schedule item (tasks, time, penalties, etc.)
   */
  private handleScheduleItemUpdate(pendingItem: IPendingItem): void {
    if (!pendingItem.item) return;
    const info = this.settings.getScheduleInfo();
    const match = info.findIndex((i) => i.key === pendingItem.item?.key);
    if (match === -1) return;
    // Merge new field values into the existing schedule entry, preserving
    // scheduler-managed state (approvalId, isPaused, isComplete, etc.)
    const updated = { ...info[match], ...pendingItem.item } as ISchedulerTimerInfo;
    if (pendingItem.date) {
      updated.startTime = new Date(pendingItem.date);
    }
    this.settings.updateSchedulerState(updated);
    this.settings.auditUser(
      new AuditMessage(
        AuditActionTypes.Event,
        "Pending Items",
        `Owner Updated Scheduled Item Details: ${pendingItem.item.name}`,
        TraceLevel.info
      )
    );
  }

  /**
   * Handles day refresh request
   */
  private handleDayRefresh(): void {
    this.settings.auditUser(
      new AuditMessage(
        AuditActionTypes.Task,
        "Pending Items",
        "Restarting Day on request",
        TraceLevel.info
      )
    );

    // Evict and reload today's custom schedule detail cache so that any changes the
    // admin made to the schedule (e.g. adding/changing the outfit event) are picked
    // up by initialiseNewDay rather than using a potentially stale cached version.
    const todayDate = new Date();
    const todayDay = convertToEventDayFromDate(todayDate.getDay());
    const todaySchedule = this.settings.getScheduledCustomSchedule(todayDay, todayDate);
    const refreshAndRoll = async () => {
      if (todaySchedule?.key) {
        this.settings.evictCustomScheduleDetailCache(todaySchedule.key);
        await this.settings.loadCustomScheduleDetail(todaySchedule.key);
      }
      const rollResults = this.tidyTaskListOnRoll();
      await this.initialiseNewDay({ ...rollResults, penalties: [], bankCharge: 0 });
    };
    refreshAndRoll().catch((error) => {
      console.error("Failed to initialize new day:", error);
      this.settings.auditUser(
        new AuditMessage(
          AuditActionTypes.Session,
          "Initialize Day",
          `Failed to initialize new day: ${error instanceof Error ? error.message : String(error)}`,
          TraceLevel.error
        )
      );
    });
  }

  /**
   * Handles settings refresh request
   */
  private handleSettingsRefresh(): void {
    this.settings.auditUser(
      new AuditMessage(
        AuditActionTypes.Task,
        "Pending Items",
        "Refreshing Settings on request",
        TraceLevel.info
      )
    );
    this.settings.refreshSettings().catch((error) => {
      console.error("Failed to refresh settings:", error);
      this.settings.auditUser(
        new AuditMessage(
          AuditActionTypes.Session,
          "Refresh Settings",
          `Failed to refresh settings: ${error instanceof Error ? error.message : String(error)}`,
          TraceLevel.error
        )
      );
    });
  }

  /**
   * Processes a single pending item based on its update type
   */
  private processPendingItem(pendingItem: IPendingItem): void {
    this.settings.updatePendingItems(pendingItem, true);
    this.refreshPendingItemData(pendingItem);

    this.settings.auditUser(
      new AuditMessage(
        AuditActionTypes.Task,
        "Pending Item",
        pendingItem.item
          ? `Handling Pending Item ${pendingItem.item?.name}`
          : `Handling Pending Item ${JSON.stringify(pendingItem)}`,
        TraceLevel.info
      )
    );

    switch (pendingItem.updateType) {
      case PendingUpdateType.createPreview:
        this.handleCreatePreview(pendingItem);
        break;
      case PendingUpdateType.proofRequest:
        this.handleProofRequestPending(pendingItem);
        break;
      case PendingUpdateType.permRelease:
        this.handlePermissionRelease(pendingItem);
        break;
      case PendingUpdateType.permissionChange:
        this.handlePermissionChange(pendingItem);
        break;
      case PendingUpdateType.remove:
        this.handleItemRemoval(pendingItem);
        break;
      case PendingUpdateType.update:
        this.handleItemUpdate(pendingItem);
        break;
      case PendingUpdateType.add:
        if (pendingItem.item) {
          this.handleItemAddition(pendingItem);
        } else if (pendingItem.gameRequest) {
          this.handleGameScheduling(pendingItem);
        }
        break;
      case PendingUpdateType.refresh:
        this.handleDayRefresh();
        break;
      case PendingUpdateType.refreshSettings:
        if (this.handledFirstPendingItemParse) {
          this.handleSettingsRefresh();
        }
        break;
      case PendingUpdateType.settingsItemUpdate:
        if (this.handledFirstPendingItemParse && pendingItem.item) {
          this.settings.applyOwnerSettingsItem(pendingItem.item);
        }
        break;
      case PendingUpdateType.scheduleRemove:
        this.handleScheduleItemRemoval(pendingItem);
        break;
      case PendingUpdateType.scheduleReschedule:
        this.handleScheduleItemReschedule(pendingItem);
        break;
      case PendingUpdateType.scheduleItemUpdate:
        this.handleScheduleItemUpdate(pendingItem);
        break;
    }
  }

  /**
   * Fired every minute and on start of day initialisation to pick up requests from owner to update
   * session and make changes to stateful tasks which are already in progress or are pending.
   * This is done on the user session so that only the user controls their own state to avoid conflicts
   * with owners and members both updating state at the same time.
   * @param overrideSOD allows pending tasks to be flushed out during roll process
   * @param handleRemovalsOnly if true, only process removal requests
   */
  private handlePendingItems = (
    overrideSOD?: boolean,
    handleRemovalsOnly?: boolean
  ): void => {
    // Guard clauses
    if (this.pendingItemsTransaction) return;
    if (this.settings.pendingRoll()) return;
    if (!overrideSOD && this.state === SchedulerState.RollingDay) return;

    // Get and filter items to process
    const pendingItems = this.settings.getPendingState();
    const canPerform = this.settings.canPerformSomeTasksForToday();
    const toProcess = this.filterPendingItems(pendingItems, handleRemovalsOnly || false, canPerform);

    if (toProcess.length === 0) return;

    // Start transaction
    this.pendingItemsTransaction = uuid();
    this.settings.auditUser(
      new AuditMessage(
        AuditActionTypes.Task,
        "Pending Items",
        `Handling Pending ${toProcess.length} Items`,
        TraceLevel.info
      )
    );

    // Process each item
    try {
      for (let i = 0; i < toProcess.length; i += 1) {
        this.processPendingItem(toProcess[i]);
      }
    } finally {
      // Always clear the transaction lock so future calls can proceed
      this.pendingItemsTransaction = undefined;
      this.handledFirstPendingItemParse = true;
    }
  };

  private addPendingItemToBasket = (
    pendingItem: IPendingItem,
    scheduleReason: string
  ) => {
    if (!pendingItem.item) {
      this.settings.auditUser(
        new AuditMessage(
          AuditActionTypes.Task,
          "Queued Up Pending Item",
          "Unable to find pending item to add",
          TraceLevel.warning
        )
      );
      return; // already have it - don't override existing items
    }

    const taskSchedule = this.settings.getTaskSchedule();
    if (
      taskSchedule[pendingItem.item.key] &&
      (taskSchedule[pendingItem.item.key] as ITaskTimerInfo).permanent
    ) {
      this.settings.auditUser(
        new AuditMessage(
          AuditActionTypes.Task,
          pendingItem.item.name,
          "Item already in daily schedule as permanently applied, ignoring add request",
          TraceLevel.warning
        )
      );
      return; // already have it - don't override existing items
    }
    if (
      pendingItem.itemAdditionOptions &&
      pendingItem.itemAdditionOptions.isPillory
    ) {
      pendingItem.item = this.settings.getPenalty(pendingItem.item.key); /// refresh the item sent from discord which is cut down
    }
    if (!pendingItem.item) {
      this.settings.auditUser(
        new AuditMessage(
          AuditActionTypes.Task,
          "Error finding pending item",
          "Unable to find pending item to add to scheduler",
          TraceLevel.error
        )
      );
      return;
    }
    this.onAddToSchedule(
      pendingItem.item,
      scheduleReason,
      pendingItem.itemAdditionOptions
    );
    this.settings.auditUser(
      new AuditMessage(
        AuditActionTypes.Session,
        pendingItem.item.name,
        "Owner Item Added Item",
        TraceLevel.info
      )
    );
  };

  // queues up a credit to run for the day
  public onSODAddRequiredRandomTasks = (customDay = false): void => {
    this.taskSelectionManager.onSODAddRequiredRandomTasks(customDay);
  };

  // every second we want to update the cookie state for the position and not call the db to persist
  public handlePositionTick = (): void => {
    if (this.isDisposed) return;
    this.positionTracker.handlePositionTick();
  };

  private handlePositionFailure(): number {
    const result = this.positionTracker.handlePositionFailed();
    return result.penalty;
  }

  public handleItemFailed = (item: ISettingsItem): void => {
    let penalty = 0;
    // we can fail a position, task or permission
    if (item.itemType === ItemTypes.task) {
      this.handleCreditItemReset(item);
      const credit = this.settings.getTask(item.key);
      penalty = credit ? credit.credit * 2 : 0;
    } else if (item.itemType === ItemTypes.penalty) {
      this.handleCreditItemReset(item);
      penalty = this.settings.getBankBalance() * 0.5;
    } else if (item.itemType === ItemTypes.position) {
      // if position end timer and reset for the current item to start again
      penalty = this.handlePositionFailure();
    } else if (item.itemType === ItemTypes.permission) {
      // if permission mark isAllowed to false and stop any timer ticks and remove endtimes - treat as frequency once
      this.onPermissionRequested(item.key, false, {
        itemKey: item.key,
        decision: false,
        reason: "Failed - no longer available",
      });
      const permission = this.settings.getPermission(item.key);
      penalty = ((permission && permission.requiredCredit) || 2) * 2;
    }
    const displayName = item.name;
    this.settings.auditUser(
      new AuditMessage(
        AuditActionTypes.Session,
        displayName,
        "User failed task in progress.",
        TraceLevel.warning,
        false,
        Math.abs(penalty) * -1
      )
    );
    this.settings.decrementBank(penalty, "Item Failure " + displayName);
    this.settings.addPenaltyPoint("Item Failure " + displayName);
    const failedText = `${this.settings.getControl().username
      } failed to complete ${getItemTypeDisplayName(
        item.itemType
      )} - ${displayName}. Session has been penalised by £${penalty} and 1 penalty point as a result.`;
    // send report to owner if owned
    if (!this.settings.getControl().isOwnedBySelf()) {
      sendMessageToOwner(
        MessageType.FailedItem,
        this.settings,
        failedText,
        `Failed ${getItemTypeDisplayName(item.itemType)} - ${displayName}`
      );
    }
    sendFailedMessage(this.settings, failedText);
  };

  // the new day, where the day in the month is different to last encountered signals that the start of day can be scheduled to fire
  // and the audit and logs can roll to the new day. checks for task completions, reports, etc are now postponed until the start of day
  private checkIfNewDay = async (): Promise<boolean> => {
    return this.dayRollManager.checkIfNewDay();
  };

  startOfDayTime: Date | undefined;
  public onStartOfDay = async (rollState: RollState): Promise<void> => {
    await this.dayRollManager.onStartOfDay(
      rollState,
      { value: this.state, setValue: (value: string) => { this.state = value as any; } },
      { value: this.startOfDayTime, setValue: (value: Date | undefined) => { this.startOfDayTime = value; } }
    );
  };

  private determineDaysMissed = (rollState: RollState): number => {
    return this.dayRollManager.determineDaysMissed(rollState);
  };

  private initialiseNewDay = async (
    rollResults?: IRollResults
  ): Promise<void> => {
    if (this.state === SchedulerState.InitialisingDay) return;
    this.state = SchedulerState.InitialisingDay;
    if (this.settings.checkHolidayToday()) {
      this.resetDailyState(undefined, true); // clear states but leave tasks as is
      this.state = SchedulerState.Ready;
      return;
    }
    this.resetDailyState(rollResults?.seedCredits);
    // you want to do this prior to penalty points being spent as the penalties might be added from pillories
    this.handleCompletedPillories(true);
    await this.spendPenaltyPoints(rollResults?.penalties);

    const today = new Date();
    const dateDay = convertToEventDayFromDate(today.getDay());
    const scheduledCustomKey = this.settings
      .getScheduledCustomSchedule(dateDay, today)?.key;
    if (scheduledCustomKey) {
      // Ensure custom-schedule overrides (including report settings) are available
      // before scheduling morning/daily report events for this day.
      await this.settings.loadCustomScheduleDetail(scheduledCustomKey);
    }

    this.setupMorningReport();
    this.setUpDailyReport();

    const customSchedule = await this.settings.getScheduledCustomSchedule(dateDay, today);
    const isCustomSchedule = customSchedule !== undefined;
    this.settings.updateState(STATE_KEY_CUSTOM_DAY, isCustomSchedule);

    // handle positions even on custom schedule days
    this.refreshPosition();
    this.refreshMetronome();
    this.refreshSiteEffects();
    this.checkForTaskReceiptUnlocks(true);
    this.settings.checkCanUnlockSession();

    const isPreviewed = this.settings.unpackPreviewToday();
    this.handlePendingItems(true); // add in the explict items requested
    // now add in anything that events want to add - if custom schedule use those, otherwise use the settings
    if (isCustomSchedule) this.setupCustomScheduleEvents(customSchedule);
    else this.setupAutoScheduledEvents(); // no point doing this intra day - just set up on tick

    // first add in anything from the owner
    this.tidyPendingItems(); // clears out all the old requests for the old state from yesterday

    const promises = [];
    // right now we want to find anything that was previewed and we told the member what to take
    if (!isPreviewed) {
      // add in anything that is set with a preselect (daily/permanent) plus mandatory random tasks.
      // On custom schedule days, enforced preselect tasks still apply, but daily mandatory
      // random tasks are intentionally skipped.
      promises.push(
        this.handleEnforcedTasks(false)
          .then(() => this.onSODAddRequiredRandomTasks(isCustomSchedule))
      );
    }
    promises.push(this.changeRandomSettings());
    promises.push(
      new Promise<void>((resolve) => {
        this.setupDailyPermissions();
        resolve();
      })
    );
    promises.push(
      new Promise<void>((resolve) => {
        this.sendOwnerWfhMessage();
        resolve();
      })
    );
    await Promise.all(promises);
    this.settings.handlePreviewDayOnRoll();
    this.settings.handleSendRequiredEventsOnline();
    this.state = SchedulerState.Ready;
  };

  private tidyPendingItems = (): void => {
    const pendingItems = this.settings.getPendingState();
    // only adds or queued up custom schedules make sense after roll to add in
    const toRemove = pendingItems.filter(
      (item) =>
        item.updateType !== PendingUpdateType.add &&
        item.updateType !== PendingUpdateType.scheduleChange
    );
    toRemove.forEach((item) => this.settings.updatePendingItems(item, true));
  };

  private sendOwnerWfhMessage = (): void => {
    if (
      this.settings.getControl().isOwned() &&
      !this.settings.getIsWeekend() &&
      this.settings.canPerformAllTasksForDate(new Date())
    )
      sendMessageToOwner(
        MessageType.WorkingHomeNotification,
        this.settings,
        `Member ${this.settings.getControl().username
        } has indicated they are at available today. All tasks set for Free Days are enabled for them to complete today. If they are unable to perform home tasks, please request they update their Work Days in the Application.`,
        `${this.settings.getControl().username} is working at home today`
      );
  };

  private setupDailyPermissions = (): void => {
    const permissionSettings = this.settings.getPermissionSettings();
    this.settings.auditUser(
      new AuditMessage(
        AuditActionTypes.DailyChecks,
        "Setup Daily Permissions",
        `disablePermissions=${permissionSettings?.disablePermissions}, canPerform=${this.settings.canPerformSomeTasksForToday()}`,
        TraceLevel.info
      )
    );
    if (
      !permissionSettings ||
      permissionSettings.disablePermissions ||
      !this.settings.canPerformSomeTasksForToday()
    )
      return;
    let availablePermissions = this.settings.getPermissions(
      (p) => isAvailableItem(p) && p.eventType === EventType.standard
    );
    availablePermissions = availablePermissions.filter(
      (p) =>
        this.settings.scheduleItemAvailable(p) &&
        this.settings.itemAvailableForDay(
          p,
          convertToEventDayFromDate(new Date().getDay()),
          false,
          false
        )
    );
    const bank = this.settings.getBankBalance();
    for (let i = 0; i < availablePermissions.length; i++) {
      const permission = availablePermissions[i];
      if (permission.requiredCredit && permission.requiredCredit > bank) {
        this.settings.auditUser(
          new AuditMessage(
            AuditActionTypes.Permission,
            "Permission Denied",
            `Unable to afford permission ${permission.name} as £${permission.requiredCredit} is required and member only has £${bank}`,
            TraceLevel.warning
          )
        );
        continue;
      }
      const permissionRequestChance = permission.approvalChance;
      const chanceDecision =
        getDecimalRandom(0, 1) > 1 - permissionRequestChance;
      this.handlePermissionResponse({
        itemKey: permission.key,
        decision: chanceDecision,
      });
    }
  };

  private tidyTaskListOnRoll = (): IRollResults => {
    const penalties: { reason: string; points: number }[] = [];
    const seedCredits: ITaskBasketInfo = {};
    let bankCharge = this.checkPositionCompletion(penalties);
    bankCharge += this.checkForTasksCompletion(seedCredits, penalties);
    bankCharge += this.checkPenaltyCompletion(seedCredits, penalties);
    bankCharge += this.checkReportCompletion(penalties);
    bankCharge += this.handleOwnerTax();
    return { seedCredits, penalties, bankCharge };
  };

  private resetDailyState = (
    seedCredits?: ITaskBasketInfo,
    onHoliday?: boolean
  ): void => {
    if (!onHoliday) this.settings.resetTaskState(seedCredits || {});
    //credits done via penalty refresh as credits may not be empty
    this.settings.resetPermissionState({});
    this.settings.updatePositionState({} as IPositionBasketInfo);
    this.settings.updateMetronomeState({} as IPositionBasketInfo);
    this.settings.updateSiteEffectsState({} as IPositionBasketInfo);
    this.settings.resetSchedulerState([]);
    this.settings.closeDialogs();
    this.settings.updateProofRequests(0);
  };
  private handleOwnerTax = (): number => {
    return this.dayRollManager.handleOwnerTax();
  };

  private addDailyRollPenalty = (
    penalties: { reason: string; points: number }[],
    reason: string,
    points: number
  ): void => {
    penalties.push({ reason, points });
  };

  private checkReportCompletion = (
    penalties: { reason: string; points: number }[]
  ): number => {
    return this.dayRollManager.checkReportCompletion(penalties);
  };

  private checkPenaltyCompletion = (
    seedCredits: ITaskBasketInfo,
    penalties: { reason: string; points: number }[]
  ): number => {
    return this.dayRollManager.checkPenaltyCompletion(seedCredits, penalties);
  };

  private checkForTasksCompletion = (
    seedCredits: ITaskBasketInfo,
    penalties: { reason: string; points: number }[]
  ): number => {
    return this.dayRollManager.checkForTasksCompletion(seedCredits, penalties);
  };

  private checkPositionCompletion = (
    penalties: { reason: string; points: number }[]
  ): number => {
    return this.positionTracker.checkPositionCompletion(penalties);
  };

  private spendPenaltyPoints = async (
    rollPenalties?: IRollPenalty[]
  ): Promise<void> => {
    await this.penaltyManager.spendPenaltyPoints(rollPenalties);
  };

  private changeRandomSettings = (): Promise<void> => {
    return new Promise<void>((resolve) => {
      const generalSettings = this.settings.getGeneralSettings();
      if (generalSettings.randomiseSettings) {
        generalSettings.showBank = flipCoin();
        generalSettings.showRemainingTimes = flipCoin();
        generalSettings.hidePendingTasks = flipCoin();
        void this.settings
          .saveGeneralSettings(
            generalSettings,
            this.settings.getCheckoutPermission()?.requiredCredit,
            true
          )
          .then(() => {
            this.settings.auditUser(
              new AuditMessage(
                AuditActionTypes.DailyChecks,
                "General Settings Updated",
                `Show Bank: ${generalSettings.showBank}`,
                TraceLevel.info
              )
            );
            this.settings.auditUser(
              new AuditMessage(
                AuditActionTypes.DailyChecks,
                "General Settings Updated",
                `Show Remaining Times: ${generalSettings.showRemainingTimes}`,
                TraceLevel.info
              )
            );
            this.settings.auditUser(
              new AuditMessage(
                AuditActionTypes.DailyChecks,
                "General Settings Updated",
                `Hide Pending Tasks: ${generalSettings.hidePendingTasks}`,
                TraceLevel.info
              )
            );
          });
      }
      resolve();
    });
  };

  public addSystemGeneratedPenaltyItem = (
    penalty: IPenaltyItem,
    reason: string
  ): void => {
    this.penaltyManager.addSystemGeneratedPenaltyItem(penalty, reason);
  };

  private setupAutoScheduledEvents = (): void => {
    this.eventScheduleManager.setupAutoScheduledEvents();
  };

  private setupCustomScheduleEvents = (
    customSchedule: ICustomSchedule
  ): void => {
    this.eventScheduleManager.setupCustomScheduleEvents(customSchedule);
  };

  public refreshStartOfDaySchedule = (eventItem: IEventItem): void => {
    this.settings.deleteSchedulerState(eventItem.key);
    this.addEventToSchedule(
      eventItem,
      "Start Of Day has been requested to be re-run for this session and has been queued up the the admin team."
    );
  };

  private handlePermissionResponse = ({
    itemKey,
    decision,
    decisionResponse,
  }: IEventDecisionResponse): void => {
    // if the response is a simple yes no then generate the rest ourselves based on the response
    if (!decisionResponse) {
      this.onPermissionRequested(itemKey, decision);
      return;
    }

    // if the response is specific then we set up the event task list accordingly
    decisionResponse.decision = decision;
    const permission = this.eventProcessor.onPermissionRequested(itemKey, decision, decisionResponse);
    if (!permission) return;
  };

  public saveDailyReport = (report: string): void => {
    this.settings.saveDailyReport(
      report,
      "Daily Report - " + formatValidDate(new Date(), "dd MMM yyyy")
    );
    const credit = this.settings.getGeneralSettings().dailyReportingCredit;
    this.auditIncrement(
      "Daily Report",
      "Completed",
      this.settings.incrementBank(
        this.settings.getReward(Math.abs(credit)),
        "Report"
      )
    );
  };

  public saveMorningReport = (report: string): void => {
    this.settings.saveMorningReport(
      report,
      "Morning Message - " + formatValidDate(new Date(), "dd MMM yyyy")
    );
  };

  public saveLockResult = (lockState: IRequestLockState): void => {
    this.settings.updateRequestLockDialog(false);
    try {
      if (lockState.isPaused) {
        this.settings.handleRelockRequest(lockState);
      } else if (lockState.captureUpload) {
        this.startTaskTimer(lockState.item, true, false);
      } else {
        this.settings.endLock(lockState);
      }
    } catch (error) {
      this.showMessage({
        message: 'Failed to process lock result. Please refresh the page and try again.',
        error: true,
        persist: true,
        notificationType: NotificationType.ErrorInfo,
      });
      void this.settings.auditUser(
        new AuditMessage(
          AuditActionTypes.Session,
          'saveLockResult',
          `Error processing lock result: ${error instanceof Error ? error.message : String(error)}`,
          TraceLevel.error
        )
      );
    }
  };

  public sendLines = (
    lines: string,
    line: string,
    expectedCount: number,
    mistakes: number,
    taskKey: string,
    allowRedoLines: boolean,
    stats: ITypingStats
  ): void => {
    this.taskManager.sendLines(
      lines,
      line,
      expectedCount,
      mistakes,
      taskKey,
      allowRedoLines,
      stats,
      sendLinesToOwner
    );
  };

  private handleRouletteVoteTask = (item: ITaskVoteConfig, basketItem: BaseBasketItem): void => {
    // This method is now handled internally by RouletteManager.handleRouletteResult
    // Kept as stub for backward compatibility
  }

  public handleRouletteResult = (
    item: string | ITaskItem | IPenaltyItem | ITaskVoteConfig,
    roulette: IRouletteRequest,
    options?: ItemAdditionOptions
  ): void => {
    this.rouletteManager.handleRouletteResult(item, roulette, options);
  };
}
